import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ClientPortal, PortalProject, PortalStage, PortalNote, PortalDeliverableApproval } from '../types';
import { auth, db, storage, ensureAuth } from '../utils/firebase';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, Timestamp, addDoc, arrayUnion, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getPublicSettings, AppSettings } from '../utils/appSettings';
import { useAuth } from '../utils/AuthContext';
import { isPortalStaffUser } from '../utils/userRoles';
import {
  getPortalAccessMeta,
  verifyPortalPinServer,
  savePortalSelectedServices,
  getEngagementLetterForPortal,
  getEngagementLetterBySlug,
} from '../utils/portalPinService';

import { 
  Lock, 
  FileSignature, 
  Box, 
  FileText, 
  ClipboardList, 
  Layers2,
  PenTool,
  Factory, 
  Truck, 
  X,
  CreditCard,
  ExternalLink,
  ChevronRight,
  ChevronDown,
  ArrowRight,
  ArrowUp,
  LayoutGrid,
  Loader2,
  CheckCircle,
  MessageSquare,
  Send,
  Paperclip,
  Image as ImageIcon,
  FileUp,
  User,
  Briefcase,
  Building2,
  Pencil,
  Save,
  Eye,
  Video,
  Download,
  Receipt,
  ShieldCheck,
  HelpCircle,
} from 'lucide-react';
import { formatPortalBondAmount, portalBondCopy } from '../utils/portalBondStatus';
import ProjectTimeline, { TimelinePhase, TimelineMilestone } from '../components/portal/ProjectTimeline';
import PortalReleasedVersionsPopover from '../components/portal/PortalReleasedVersionsPopover';
import PortalModelStepVersionsPopover from '../components/portal/PortalModelStepVersionsPopover';
import PortalDesignApprovalWalkthrough from '../components/portal/PortalDesignApprovalWalkthrough';
import PortalDrawingsApprovalWalkthrough, {
  drawingApprovalLabel,
  formatDrawingList,
} from '../components/portal/PortalDrawingsApprovalWalkthrough';
import '../components/portal/PortalDrawingsApprovalWalkthrough.css';
import '../components/portal/PortalModelStepVersionsPopover.css';
import '../components/portal/PortalDesignApprovalWalkthrough.css';
import DeliverableLatestVersionCell from '../components/portal/DeliverableLatestVersionCell';
import PortalPdfViewerModal from '../components/portal/PortalPdfViewerModal';
import PortalHtmlViewerModal from '../components/portal/PortalHtmlViewerModal';
import PortalModelWalkthroughRecorder from '../components/portal/PortalModelWalkthroughRecorder';
import '../components/portal/PortalModelWalkthroughRecorder.css';
import PortalDeliverableApprovalModal from '../components/portal/PortalDeliverableApprovalModal';
import '../components/portal/PortalDeliverableApprovalModal.css';
import PortalApprovalTrail, { PortalApprovalTrailBoundary } from '../components/portal/PortalApprovalTrail';
import { formatSharedModelApprovalNote } from '../utils/sharedModelApproval';
import { buildClientPortalTrailInput } from '../utils/portalApprovalTrailSource';
import { hasSeenPortalPdfMarkupTour } from '../components/portal/PortalPdfMarkupTour';
import {
  groupPortalDealApprovals,
  formatApprovalBadge,
  evaluateProposalSigningGate,
  isConceptApprovalFlowEnabled,
  isConceptReviewRetired,
  shouldSkipConceptApprovalForSigning,
  canOfferPortalSignProposal,
  isDeliverableApproved,
  isPortalProposalSigned,
  nextApprovalWithHistory,
  portalProposalCtaLabel,
  isInteractiveModelApproved,
  isPreviewImageApproved,
  needsInteractiveModelDeliverable,
  needsPreviewImageDeliverable,
  needsConceptPdfDeliverable,
  CONCEPT_MODEL_APPROVAL_DISCLAIMER,
  PORTAL_FOCUS_CONCEPT_MODEL,
} from '../utils/portalDeliverableApprovalService';
import {
  subscribePortalReleasesForDeal,
  subscribePortalBundlesForDeal,
  mergeBundlesWithReleases,
  latestPortalRelease,
  resolvePortalReleaseBundleId,
  currentPackageReleases,
  bundlePortalTarget,
} from '../utils/portalFileReleaseService';
import type { PortalFileRelease, PortalReleaseBundle } from '../types';
import { portalReleaseDesignPhase } from '../utils/portalReleasePhase';
import { portalHostedAssetHref } from '../utils/portalAssetUrl';
import { loomEmbedSrcFromPaste } from '../utils/loomEmbed';
import { clientFacingModelLoomUrl, loomUrlForRelease } from '../utils/portalModelLoom';
import {
  getPortalAgreementSnapshot,
  snapshotShowsDesignFeePaid,
} from '../utils/portalDesignFeePaid';
import DesignFeeSignModal from '../components/portal/DesignFeeSignModal';
import { portalHasModelContent } from '../utils/portalStageProgress';
import { isModelWalkthroughEnabled } from '../utils/portalWalkthroughFeature';
import {
  computePortalStepStatuses,
  signalsFromPortalProject,
  type PortalStepStatus,
} from '../utils/portalStageEngine';
import {
  buildEffectivePortalProject,
  enrichPortalWithReleases,
  reconcileAndPersistPortalProjects,
  diffPortalProjectReconciliation,
  reconcilePortalProjectsFromLead,
  isMultiProjectPortal,
  mergePortalFromFirestore,
  parsePortalProjectRow,
  sortVisiblePortalProjects,
} from '../utils/portalProjectView';
import { generateSignedAgreementPdf } from '../utils/generateSignedAgreementPdf';
import { subscribeChangeOrdersForDeal, getChangeOrderReference } from '../utils/changeOrders';
import { sumApprovedChangeOrders } from '../utils/contractValue';
import { buildPaymentScheduleBreakdown, invoicedInstallmentLocks } from '../utils/paymentSchedule';
import PaymentScheduleBreakdown from '../components/proposals/PaymentScheduleBreakdown';
import PortalStatementButton from '../components/portal/PortalStatementButton';
import { getSignedCommercialListTotal } from '../utils/proposalPublicGrandTotals';
import type { ProposalBuilderData } from '../types';
import PortalPhaseSummaryCard from '../components/portal/PortalPhaseSummaryCard';
import { isPhasedProposal } from '../utils/proposalPhases';
import {
  loadSupplementaryStreamSections,
  type PortalSupplementarySection,
} from '../utils/portalSupplementaryStreams';
import ChangeOrderInternalBadge, { isInternallyApprovedChangeOrder } from '../components/proposals/ChangeOrderInternalBadge';
import '../components/proposals/ChangeOrder.css';
import type { ChangeOrder } from '../types';
import {
  beginPortal3dModelExternalTabSession,
  logPortalActivity,
  usePortalSession,
  resolvePortalActivitySource,
  setPortalActivityStakeholderDisplayName,
  setPortalActivityStakeholderLeadId,
  setPortalActivityStakeholderLinkId,
} from '../utils/portalActivityLogger';
import {
  verifyStakeholderTokenCallable,
  clearStakeholderSessionCallable,
  readStakeholderClaimsFromToken,
} from '../utils/stakeholderAuth';
import { SAMPLE_GC_MODEL_PAGE_TITLE, SAMPLE_GC_PORTAL_SLUG, isSampleGcPortalSlug } from '../utils/sampleGcPortal';
import './ClientPortalSplash.css';

// Timeline step configuration
interface TimelineStep {
  id: PortalStage;
  number: number;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const TIMELINE_STEPS: TimelineStep[] = [
  {
    id: 'design-fee',
    number: 1,
    title: 'Concept Design Fee',
    description: 'Initial design engagement & payment',
    icon: <FileSignature size={22} />,
  },
  {
    id: '3d-model',
    number: 2,
    title: 'Concept Deliverables',
    description: '3D model and concept drawings for your review',
    icon: <Box size={22} />,
  },
  {
    id: 'quote',
    number: 3,
    title: 'Your Quote',
    description: 'Custom proposal for your project',
    icon: <ClipboardList size={22} />,
  },
  {
    id: 'quote-signed',
    number: 4,
    title: 'Quote Signed',
    description: 'Agreement confirmed',
    icon: <PenTool size={22} />,
  },
  {
    id: '2d-plans',
    number: 5,
    title: 'Plans',
    description: 'Construction plans for your project',
    icon: <FileText size={22} />,
  },
  {
    id: 'in-production',
    number: 6,
    title: 'In Production',
    description: 'Your timber frame is being crafted',
    icon: <Factory size={22} />,
  },
  {
    id: 'delivery',
    number: 7,
    title: 'Delivery & Install',
    description: 'Final delivery and installation',
    icon: <Truck size={22} />,
  },
];

// Step status comes from the shared portal stage engine so the client-facing
// timeline can never disagree with the staff workspace.
type StepStatus = PortalStepStatus;

function isProposalVisibleToClient(status: string | null | undefined): boolean {
  return status === 'sent' || status === 'opened' || status === 'approved';
}

/**
 * Resolve the display pieces for a portal project. Prefers structured fields (set by the CRM
 * project-naming tool). Falls back to stripping `#number` and the portal's client company from
 * the legacy concatenated `projectName` so existing portals still display just the descriptor.
 */
function getPortalProjectDisplay(
  project: { projectName?: string; descriptor?: string; projectNumber?: string; clientLabel?: string } | null | undefined,
  fallbackClient?: string | null,
): { projectNumber: string | null; descriptor: string } {
  const raw = (project?.projectName || '').trim();
  const structuredDescriptor = (project?.descriptor || '').trim();
  const structuredNumber = (project?.projectNumber || '').trim();
  const clientLabel = (project?.clientLabel || fallbackClient || '').trim();

  if (structuredDescriptor) {
    const num = structuredNumber
      ? (structuredNumber.startsWith('#') ? structuredNumber : `#${structuredNumber}`)
      : (raw.match(/^(#\d+)/)?.[1] ?? null);
    return { projectNumber: num, descriptor: structuredDescriptor };
  }

  let rest = raw;
  let projectNumber: string | null = null;

  const numMatch = rest.match(/^(#\d+)\s*(?:[-–—·|:]\s*)?/);
  if (numMatch) {
    projectNumber = numMatch[1] ?? null;
    rest = rest.slice(numMatch[0].length).trim();
  }

  if (clientLabel) {
    const escaped = clientLabel.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`^${escaped}\\b\\s*(?:[-–—·|:]\\s*)?`, 'i');
    rest = rest.replace(re, '').trim();
  }

  return { projectNumber, descriptor: rest || raw || 'Project' };
}

/** Coerce Firestore branding fields that may be stored as plain URL strings. */
function normalizeBrandingMediaItem(raw: unknown, defaultName: string): { url: string; name: string } | null {
  if (raw == null) return null;
  if (typeof raw === 'string' && raw.trim()) {
    return { url: raw.trim(), name: defaultName };
  }
  if (typeof raw === 'object' && raw !== null && 'url' in (raw as object)) {
    const u = (raw as { url?: unknown }).url;
    const n = (raw as { name?: unknown }).name;
    if (typeof u === 'string' && u.trim()) {
      return {
        url: u.trim(),
        name: typeof n === 'string' && n.trim() ? n.trim() : defaultName,
      };
    }
  }
  return null;
}

/**
 * Portal branding uses only the CRM-configured engagement logo (public settings),
 * same as before bundled fallbacks. If the URL fails to load, clear the image so
 * the wordmark / empty-state text shows instead of a wrong substitute.
 */
function useResolvedPortalBrandingLogo(
  remoteLogo: { url: string; name: string } | null
): { src: string | null; onImgError: () => void } {
  const remoteUrl = remoteLogo?.url?.trim() || null;
  const [remoteFailed, setRemoteFailed] = useState(false);

  useEffect(() => {
    setRemoteFailed(false);
  }, [remoteUrl]);

  const src = remoteUrl && !remoteFailed ? remoteUrl : null;
  const onImgError = useCallback(() => {
    setRemoteFailed(true);
  }, []);

  return { src, onImgError };
}

const ClientPortalSplash: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  /** Staff viewing the portal (preview from CRM or direct): bypass PIN and log activity as employee. */
  const isStaffPortalUser = !!user && isPortalStaffUser(user.role);
  const [paymentStatus, setPaymentStatus] = useState<'success' | 'cancelled' | null>(null);
  const [portal, setPortal] = useState<ClientPortal | null>(null);
  const [, setPortalId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Settings
  const [engagementLogo, setEngagementLogo] = useState<{ url: string; name: string } | null>(null);
  const { src: brandingLogoSrc, onImgError: onBrandingLogoError } = useResolvedPortalBrandingLogo(engagementLogo);
  const [engagementBackground, setEngagementBackground] = useState<{ url: string; name: string } | null>(null);
  const [brandVideoUrl, setBrandVideoUrl] = useState('');
  const [engagementLetterType, setEngagementLetterType] = useState<'concept' | 'retainer' | null>(null);
  const [engagementSigned, setEngagementSigned] = useState(false);
  const [portalOverlayColor, setPortalOverlayColor] = useState('#ffffff');
  const [portalOverlayOpacity, setPortalOverlayOpacity] = useState(0.92);
  const [globalPortalBgImage, setGlobalPortalBgImage] = useState<{ url: string; name: string } | null>(null);
  const [sample3DModelUrl, setSample3DModelUrl] = useState<string | null>(null);

  // Design fee pricing
  const DEFAULT_FEE_SERVICES = useMemo(() => [
    { id: '3d-model', name: '3D Concept Model', price: 1300, description: 'We need this to quote your project — you\'ll get an interactive 3D model you can rotate, zoom into, and explore from every angle.', required: true },
    { id: '2d-elevations', name: '2D Elevation Drawings', price: 850, description: 'Printable 2D plans of your frame. Not required for your quote, but recommended for complex projects where your builder or engineer needs detailed elevation references.' },
    { id: 'floor-plan', name: 'Floor Plan', price: 750, description: 'A layout of the room and space around your frame. Not required for your quote, but helpful if you want to plan furniture placement and visualize how the space will flow.' },
  ], []);
  const [designFeeServicesRaw, setDesignFeeServicesRaw] = useState<{ id: string; name: string; price: number; description: string; required?: boolean }[]>(DEFAULT_FEE_SERVICES);
  const globalFeeServicesRef = useRef(DEFAULT_FEE_SERVICES);

  const designFeeServices = useMemo(() => {
    const defaultMap = new Map(DEFAULT_FEE_SERVICES.map(s => [s.id, s]));
    return designFeeServicesRaw.map(svc => {
      const def = defaultMap.get(svc.id);
      return def ? { ...svc, description: def.description, required: def.required ?? svc.required } : svc;
    });
  }, [designFeeServicesRaw, DEFAULT_FEE_SERVICES]);
  const [selectedServices, setSelectedServices] = useState<string[]>(['3d-model']);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showServiceSelection, setShowServiceSelection] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [showSample3D, setShowSample3D] = useState(false);
  const [showWalkthroughRecorder, setShowWalkthroughRecorder] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [directCheckoutLoading, setDirectCheckoutLoading] = useState(false);
  const [awaitingPayment, setAwaitingPayment] = useState(false);
  const paymentUnsubRef = useRef<(() => void) | null>(null);
  
  // Handle payment return from Stripe + restore project from URL
  const paymentReturnHandled = useRef(false);
  useEffect(() => {
    const payment = searchParams.get('payment');
    const projectParam = searchParams.get('project');
    if (projectParam) {
      setSelectedProjectId(projectParam);
    }
    if (payment === 'success' || payment === 'cancelled') {
      setPaymentStatus(payment);
      const cleaned = new URLSearchParams(searchParams);
      cleaned.delete('payment');
      setSearchParams(cleaned, { replace: true });
      if (payment === 'success') {
        paymentReturnHandled.current = true;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, []); // Run once on mount

  // After successful payment return, poll Firestore for webhook completion
  useEffect(() => {
    if (!paymentReturnHandled.current || !portal?.id) return;
    paymentReturnHandled.current = false;

    const projectId = searchParams.get('project') || selectedProjectId;
    const isPaid = snapshotShowsDesignFeePaid(portal as unknown as Record<string, any>, projectId);

    if (isPaid) return;

    const unsub = onSnapshot(doc(db, 'clientPortals', portal.id), (snap) => {
      const data = snap.data();
      if (!data) return;
      const paid = snapshotShowsDesignFeePaid(data, projectId);
      if (paid) {
        const paidAmount = projectId
          ? (data.projects?.[projectId]?.amountPaid || data.amountPaid)
          : data.amountPaid;
        setPortal(prev => {
          if (!prev) return prev;
          const next = { ...prev, designFeePaid: true, designFeePaidAt: new Date(), amountPaid: paidAmount, currentStage: '3d-model' as const };
          if (projectId && next.projects?.[projectId]) {
            next.projects = { ...next.projects };
            next.projects[projectId] = {
              ...next.projects[projectId],
              designFeePaid: true,
              designFeePaidAt: new Date(),
              amountPaid: paidAmount,
              currentStage: '3d-model' as const,
            };
          }
          return next;
        });
        unsub();
      }
    });

    const timeout = setTimeout(() => unsub(), 120_000);
    return () => { unsub(); clearTimeout(timeout); };
  }, [portal?.id]);

  // PIN state
  const [requiresPin, setRequiresPin] = useState(false);
  const [pinDigits, setPinDigits] = useState(['', '', '', '']);
  const [pinError, setPinError] = useState('');
  const [verifyingPin, setVerifyingPin] = useState(false);
  const [storedPin, setStoredPin] = useState<string | null>(null);
  const pinInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Safety net: if PIN screen showed before auth finished, clear it once staff user is known
  useEffect(() => {
    if (!authLoading && isStaffPortalUser && requiresPin && slug) {
      sessionStorage.setItem(`portal_verified_${slug}`, 'true');
      setRequiresPin(false);
      const refetch = async () => {
        try {
          // Load by portalId (slug list queries are denied under PIN-scoped rules).
          const accessMeta = await getPortalAccessMeta(slug);
          const snap = await getDoc(doc(db, 'clientPortals', accessMeta.portalId));
          if (snap.exists()) {
            await loadPortalData(snap.id, snap.data());
          }
        } catch (err) { console.error('Error re-fetching after PIN bypass:', err); }
      };
      refetch();
    }
  }, [authLoading, isStaffPortalUser, requiresPin, slug]);
  
  // Notes state (per-step feedback)
  const [portalNotes, setPortalNotes] = useState<PortalNote[]>([]);
  const [noteInputs, setNoteInputs] = useState<Record<string, string>>({});
  const [submittingNoteStep, setSubmittingNoteStep] = useState<string | null>(null);
  const notesEndRefs = useRef<Record<string, HTMLDivElement | null>>({});
  
  // File upload state (Step 1 - client uploads)
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Proposal status (to hide quote when draft)
  const [proposalStatus, setProposalStatus] = useState<string | null>(null);
  const [proposalApprovedAt, setProposalApprovedAt] = useState<Date | null>(null);
  const [proposalApprovedByName, setProposalApprovedByName] = useState<string | null>(null);
  // Proposal ROM pricing flag (for Your Quote card badge)
  const [proposalIsRom, setProposalIsRom] = useState(false);
  // Fallback: proposal slug fetched from proposalBuilders when portal doesn't have it
  const [resolvedProposalSlug, setResolvedProposalSlug] = useState<string | null>(null);
  // Change orders for the selected project (client-visible: sent/approved only)
  const [changeOrders, setChangeOrders] = useState<ChangeOrder[]>([]);

  // Multi-project state
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [projectsList, setProjectsList] = useState<PortalProject[]>([]);

  const [stakeholderDisplayName, setStakeholderDisplayName] = useState<string | null>(null);
  const [stakeholderCompanyName, setStakeholderCompanyName] = useState<string | null>(null);
  const [stakeholderLeadId, setStakeholderLeadId] = useState<string | null>(null);
  const [stakeholderLinkId, setStakeholderLinkId] = useState<string | null>(null);
  const stakeholderTokenParam = searchParams.get('stakeholderToken');
  const resolvedStakeholderToken =
    stakeholderTokenParam || (slug ? sessionStorage.getItem(`portal_stakeholder_token_${slug}`) : null);
  const isStakeholderViewer = stakeholderDisplayName !== null;
  const isSampleGcPortal = isSampleGcPortalSlug(slug);

  const activitySource = useMemo(
    () =>
      resolvePortalActivitySource({
        user,
        isStakeholderViewer,
      }),
    [user, isStakeholderViewer],
  );

  useEffect(() => {
    setPortalActivityStakeholderDisplayName(stakeholderDisplayName);
    return () => setPortalActivityStakeholderDisplayName(null);
  }, [stakeholderDisplayName]);

  useEffect(() => {
    setPortalActivityStakeholderLeadId(stakeholderLeadId);
    return () => setPortalActivityStakeholderLeadId(null);
  }, [stakeholderLeadId]);

  useEffect(() => {
    setPortalActivityStakeholderLinkId(stakeholderLinkId);
    return () => setPortalActivityStakeholderLinkId(null);
  }, [stakeholderLinkId]);

  // Portal activity session tracking (heartbeats + open/close)
  usePortalSession(portal?.id, activitySource, selectedProjectId, !authLoading && !!portal?.id);

  // Log project navigation (card click, URL restore, back to list) in one place.
  const projectNavReadyRef = useRef(false);
  const prevSelectedProjectRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!portal?.id || projectsList.length === 0) return;

    const prev = prevSelectedProjectRef.current;
    if (prev === selectedProjectId) return;

    const projectNameFor = (dealId: string | null | undefined) =>
      dealId ? projectsList.find(p => p.dealId === dealId)?.projectName : undefined;

    if (!projectNavReadyRef.current) {
      projectNavReadyRef.current = true;
      prevSelectedProjectRef.current = selectedProjectId;
      if (projectsList.length > 1 && !selectedProjectId) {
        logPortalActivity(
          portal.id,
          null,
          'page_view',
          'viewed_project_list',
          { projectCount: projectsList.length },
          activitySource,
        );
      } else if (selectedProjectId) {
        logPortalActivity(
          portal.id,
          selectedProjectId,
          'action',
          'project_selected',
          { projectName: projectNameFor(selectedProjectId) },
          activitySource,
        );
      }
      return;
    }

    if (selectedProjectId) {
      logPortalActivity(
        portal.id,
        selectedProjectId,
        'action',
        'project_selected',
        {
          projectName: projectNameFor(selectedProjectId),
          ...(prev ? { fromProjectId: prev } : {}),
        },
        activitySource,
      );
    } else if (prev) {
      logPortalActivity(
        portal.id,
        null,
        'action',
        'returned_to_project_list',
        { fromProjectId: prev, fromProjectName: projectNameFor(prev) },
        activitySource,
      );
    }

    prevSelectedProjectRef.current = selectedProjectId;
  }, [selectedProjectId, portal?.id, projectsList, activitySource]);

  // Sync selectedProjectId to URL so sub-pages can link back
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (selectedProjectId) {
      if (params.get('project') !== selectedProjectId) {
        params.set('project', selectedProjectId);
        setSearchParams(params, { replace: true });
      }
    } else {
      if (params.has('project')) {
        params.delete('project');
        setSearchParams(params, { replace: true });
      }
    }
  }, [selectedProjectId]);

  // Documents
  const [engagementDocuments, setEngagementDocuments] = useState<Array<{ id: string; name: string; url: string }>>([]);

  // Name edit state
  const [isEditingName, setIsEditingName] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [savingName, setSavingName] = useState(false);

  // Project timeline defaults (from CRM settings)
  const [timelineDefaults, setTimelineDefaults] = useState<Array<{ id: string; label: string; durationWeeks: number; bufferWeeks: number; subPhases?: Array<{ id: string; label: string; durationWeeks: number }> }> | null>(null);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState<Array<{ url: string; name: string }>>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Load settings
  useEffect(() => {
    const loadSettings = async () => {
      await ensureAuth();
      const settings = await getPublicSettings();
      const logo = normalizeBrandingMediaItem(settings?.engagementLogo, 'Company logo');
      if (logo) setEngagementLogo(logo);
      const ebg = normalizeBrandingMediaItem(settings?.engagementBackground, 'Background');
      if (ebg) setEngagementBackground(ebg);
      
      if (settings?.portalOverlayColor) {
        setPortalOverlayColor(settings.portalOverlayColor);
      }
      if (typeof settings?.portalOverlayOpacity === 'number') {
        setPortalOverlayOpacity(settings.portalOverlayOpacity);
      }
      if (settings?.portalBackgroundImage) {
        const pbg = normalizeBrandingMediaItem(settings.portalBackgroundImage, 'Portal background');
        if (pbg) setGlobalPortalBgImage(pbg);
      }
      if (settings?.engagementDocuments) {
        setEngagementDocuments(settings.engagementDocuments);
      }
      if (settings?.engagement3DModel?.url) {
        setSample3DModelUrl(settings.engagement3DModel.url);
      }
      if (settings?.engagementVideoUrl) {
        setBrandVideoUrl(settings.engagementVideoUrl);
      }
      // Load design fee pricing config (descriptions are overridden by defaults via useMemo)
      try {
        const pricingSnap = await getDoc(doc(db, 'appSettings', 'designFeePricing'));
        if (pricingSnap.exists()) {
          const pData = pricingSnap.data();
          if (pData.services?.length) {
            globalFeeServicesRef.current = pData.services;
            setDesignFeeServicesRaw(pData.services);
          }
        }
      } catch { /* use defaults */ }

      // Load project timeline defaults
      if (settings?.projectTimelineDefaults?.phases?.length) {
        setTimelineDefaults(settings.projectTimelineDefaults.phases);
      }
    };
    loadSettings();
  }, []);

  /**
   * Live-listen to `public_settings` so branding (logo, background, overlay) updates as soon as
   * staff save in CRM or `syncPublicSettings` migrates Storage URLs — no full page refresh needed.
   */
  useEffect(() => {
    let unsub: (() => void) | undefined;
    const run = async () => {
      try {
        await ensureAuth();
        unsub = onSnapshot(doc(db, 'settings', 'public_settings'), (snap) => {
          if (!snap.exists()) return;
          const d = snap.data() as Record<string, unknown>;
          setEngagementLogo(normalizeBrandingMediaItem(d.engagementLogo, 'Company logo'));
          setEngagementBackground(normalizeBrandingMediaItem(d.engagementBackground, 'Background'));
          setGlobalPortalBgImage(normalizeBrandingMediaItem(d.portalBackgroundImage, 'Portal background'));
          if (typeof d.portalOverlayOpacity === 'number') {
            setPortalOverlayOpacity(d.portalOverlayOpacity);
          }
          if (typeof d.portalOverlayColor === 'string' && d.portalOverlayColor.trim()) {
            setPortalOverlayColor(d.portalOverlayColor.trim());
          }
          if (Array.isArray(d.engagementDocuments)) {
            setEngagementDocuments(d.engagementDocuments as Array<{ id: string; name: string; url: string }>);
          }
          if (typeof d.engagementVideoUrl === 'string') {
            setBrandVideoUrl(d.engagementVideoUrl);
          }
        });
      } catch {
        /* ignore */
      }
    };
    run();
    return () => {
      unsub?.();
    };
  }, []);

  // Fetch portal data — wait for auth so staff roles are correct (avoids PIN + wrong activity source on preview tab)
  useEffect(() => {
    if (!slug) {
      setError('Invalid portal URL');
      setLoading(false);
      return;
    }
    if (authLoading) {
      return;
    }

    const fetchPortal = async () => {
      try {
        setLoading(true);
        await ensureAuth();
        // Server meta first — never download accessPin before a verified PIN claim.
        const accessMeta = await getPortalAccessMeta(slug);
        const stakeholderKey = `portal_stakeholder_${slug}`;
        const stakeholderTok =
          searchParams.get('stakeholderToken') ||
          sessionStorage.getItem(`portal_stakeholder_token_${slug}`);
        const sessionKey = `portal_verified_${slug}`;
        const pinSession = sessionStorage.getItem(sessionKey) === 'true';
        const paymentReturn = searchParams.get('payment');
        const token = await auth.currentUser?.getIdTokenResult();
        const claimPortalId = String(token?.claims?.portalAccessPortalId || '');
        const pinClaimOk = token?.claims?.portalPinVerified === true
          && claimPortalId === accessMeta.portalId;

        if (
          accessMeta.requiresPin
          && !isStaffPortalUser
          && !stakeholderTok
          && !pinClaimOk
          && !pinSession
          && paymentReturn !== 'success'
          && paymentReturn !== 'cancelled'
        ) {
          setPortalId(accessMeta.portalId);
          setRequiresPin(true);
          setLoading(false);
          return;
        }

        // Returning visitors with sessionStorage still need a server claim for rules.
        if (accessMeta.requiresPin && pinSession && !pinClaimOk && !isStaffPortalUser && !stakeholderTok) {
          setPortalId(accessMeta.portalId);
          setRequiresPin(true);
          setLoading(false);
          return;
        }

        const applyInviteeIdentity = (res: {
          displayName?: string | null;
          companyName?: string | null;
          leadId?: string | null;
        }) => {
          const inviteeName = String(res.displayName || '').trim();
          const inviteeCompany = String(res.companyName || '').trim();
          setStakeholderDisplayName(inviteeName || null);
          setStakeholderCompanyName(inviteeCompany || null);
          setStakeholderLeadId(res.leadId || null);
          setStakeholderLinkId(stakeholderTok);
          sessionStorage.setItem(stakeholderKey, 'true');
          if (stakeholderTok) {
            sessionStorage.setItem(`portal_stakeholder_token_${slug}`, stakeholderTok);
          }
          if (inviteeName) sessionStorage.setItem(`portal_invitee_name_${slug}`, inviteeName);
          else sessionStorage.removeItem(`portal_invitee_name_${slug}`);
          if (inviteeCompany) sessionStorage.setItem(`portal_invitee_company_${slug}`, inviteeCompany);
          else sessionStorage.removeItem(`portal_invitee_company_${slug}`);
        };

        const restoreCachedInviteeIdentity = () => {
          if (!isSampleGcPortalSlug(slug)) return;
          const cachedName = sessionStorage.getItem(`portal_invitee_name_${slug}`);
          const cachedCompany = sessionStorage.getItem(`portal_invitee_company_${slug}`);
          if (cachedName) setStakeholderDisplayName(cachedName);
          if (cachedCompany) setStakeholderCompanyName(cachedCompany);
        };

        // Invite tokens FIRST — claim is required before getDoc under PIN-scoped rules.
        // Use accessMeta.portalId from the server; never slug-query clientPortals.
        let stakeholderVerified = false;
        if (stakeholderTok) {
          try {
            if (isStaffPortalUser) {
              const linkSnap = await getDoc(doc(db, 'portalStakeholderLinks', stakeholderTok));
              if (!linkSnap.exists()) {
                setError('This stakeholder link is invalid or has been revoked.');
                setLoading(false);
                return;
              }
              const link = linkSnap.data() as {
                portalId?: string;
                displayName?: string;
                companyName?: string;
                leadId?: string;
                revokedAt?: unknown;
              };
              if (link.revokedAt) {
                setError('This stakeholder link is invalid or has been revoked.');
                setLoading(false);
                return;
              }
              if (link.portalId && link.portalId !== accessMeta.portalId) {
                setError('This stakeholder link is invalid or has been revoked.');
                setLoading(false);
                return;
              }
              applyInviteeIdentity({
                displayName: link.displayName,
                companyName: link.companyName,
                leadId: link.leadId,
              });
              stakeholderVerified = true;
            } else {
              const res = await verifyStakeholderTokenCallable({
                token: stakeholderTok,
                portalId: accessMeta.portalId,
              });
              applyInviteeIdentity(res);
              stakeholderVerified = true;
            }
            if (accessMeta.requiresPin) {
              sessionStorage.setItem(`portal_verified_${slug}`, 'true');
            }
          } catch (stakeErr) {
            console.error('[Portal] Stakeholder verify failed:', stakeErr);
            setError('This stakeholder link is invalid or has been revoked.');
            setLoading(false);
            return;
          }
        }

        // No-PIN portals: grant claim on load so rules allow getDoc by portalId.
        // PIN is never sent to the browser — empty pin only accepted when portal has no PIN.
        if (
          !accessMeta.requiresPin
          && !pinClaimOk
          && !isStaffPortalUser
          && !stakeholderVerified
        ) {
          await verifyPortalPinServer(slug, '');
        }

        // Always load by portalId — slug list queries are denied under portalPin rules.
        const portalSnap = await getDoc(doc(db, 'clientPortals', accessMeta.portalId));
        if (!portalSnap.exists()) {
          setError('Portal not found');
          setLoading(false);
          return;
        }

        const portalDoc = portalSnap;
        const data = portalDoc.data();

        if (!stakeholderVerified && data.accessPin) {
          const sessionKey = `portal_verified_${slug}`;
          const pinVerified = sessionStorage.getItem(sessionKey);
          const paymentReturnAfter = searchParams.get('payment');

          if (isStaffPortalUser) {
            sessionStorage.setItem(sessionKey, 'true');
            restoreCachedInviteeIdentity();
          } else if (paymentReturnAfter === 'success' || paymentReturnAfter === 'cancelled') {
            sessionStorage.setItem(sessionKey, 'true');
          } else {
            let stakeholderOk = sessionStorage.getItem(stakeholderKey) === 'true';
            if (stakeholderOk) {
              const c = await readStakeholderClaimsFromToken();
              if (
                !c.isStakeholder
                || !c.portalId
                || c.portalId !== portalDoc.id
              ) {
                stakeholderOk = false;
                sessionStorage.removeItem(stakeholderKey);
              } else if (c.displayName) {
                applyInviteeIdentity(c);
              }
            }
            if (!pinVerified && !stakeholderOk) {
              setStoredPin(data.accessPin);
              setRequiresPin(true);
              setPortalId(portalDoc.id);
              setLoading(false);
              return;
            }
          }
        } else if (!stakeholderVerified && isStaffPortalUser) {
          restoreCachedInviteeIdentity();
        } else if (!stakeholderVerified && sessionStorage.getItem(stakeholderKey) === 'true') {
          const c = await readStakeholderClaimsFromToken();
          if (c.isStakeholder && c.portalId === portalDoc.id && c.displayName) {
            applyInviteeIdentity(c);
          }
        }

        await loadPortalData(portalDoc.id, data, searchParams.get('project'));
      } catch (err) {
        console.error('Error fetching portal:', err);
        setError('Failed to load portal');
      } finally {
        setLoading(false);
      }
    };

    fetchPortal();
  }, [slug, authLoading, isStaffPortalUser, searchParams]);
  
  const loadPortalData = async (id: string, data: any, preferredDealId?: string | null) => {
    // Parse projects map (or build from legacy flat fields)
    let projects: { [dealId: string]: PortalProject } = {};
    if (data.projects && typeof data.projects === 'object') {
      for (const [dId, pData] of Object.entries(data.projects as Record<string, unknown>)) {
        projects[dId] = parsePortalProjectRow(dId, pData as Record<string, unknown>);
      }
    }
    // Legacy fallback: only create a virtual project from flat fields for truly legacy portals
    // that have never had a projects map. If `data.projects` exists (even empty {}), it means
    // multi-project was active and all projects were intentionally deleted — don't resurrect.
    const hasProjectsMap = data.projects && typeof data.projects === 'object';
    const legacyDealId = data.dealId;
    if (legacyDealId && Object.keys(projects).length === 0 && !hasProjectsMap) {
      const now = new Date();
      projects[legacyDealId] = {
        dealId: legacyDealId,
        projectName: '', // Legacy didn't store project name separately
        proposalSlug: data.proposalSlug,
        engagementLetterId: data.engagementLetterId,
        engagementLetterSlug: data.engagementLetterSlug,
        currentStage: data.currentStage || 'design-fee',
        stageHistory: data.stageHistory || [],
        designFeeSkipped: data.designFeeSkipped || false,
        inspirationBoardPortalEnabled: !!data.inspirationBoardPortalEnabled,
        designFeePaid: data.designFeePaid || false,
        designFeePaidAt: data.designFeePaidAt?.toDate?.() || undefined,
        modelUrl: data.modelUrl,
        modelImageUrl: data.modelImageUrl,
        modelLoomUrl: data.modelLoomUrl,
        modelNotes: data.modelNotes,
        modelStepSkipped: data.modelStepSkipped || false,
        conceptApprovalFlowDisabled: !!data.conceptApprovalFlowDisabled,
        plansUrl: data.plansUrl,
        productionStatus: data.productionStatus,
        estimatedCompletion: data.estimatedCompletion?.toDate?.(),
        productionNotes: data.productionNotes,
        deliveryDate: data.deliveryDate?.toDate?.(),
        deliveryStatus: data.deliveryStatus,
        completionNotes: data.completionNotes,
        createdAt: data.createdAt?.toDate?.() || now,
        updatedAt: data.updatedAt?.toDate?.() || now,
        hiddenFromPortal: false,
      };
    }
    // Align portal.projects with enhancedDeals on this lead (drop stale keys, add missing).
    const rawProjects = { ...projects };
    projects = await reconcileAndPersistPortalProjects(id, rawProjects, data.leadId);

    const projList = sortVisiblePortalProjects(projects);
    setProjectsList(projList);
    // Multi-project: only honor explicit ?project= (or a single visible project). Never
    // default to portal.dealId when project param is absent — that breaks "View all projects"
    // (clearing ?project= re-runs fetch via searchParams deps and would re-select primary deal).
    if (preferredDealId && projList.some(p => p.dealId === preferredDealId)) {
      setSelectedProjectId(preferredDealId);
    } else if (projList.length === 1) {
      setSelectedProjectId(projList[0].dealId);
    } else {
      setSelectedProjectId(null);
    }

    const portalData: ClientPortal = {
      id,
      slug: data.slug,
      accessPin: data.accessPin,
      clientName: data.clientName,
      clientEmail: data.clientEmail,
      clientCompany: data.clientCompany || '',
      projects: Object.keys(projects).length > 0 ? projects : undefined,
      leadId: data.leadId,
      dealId: data.dealId,
      engagementLetterId: data.engagementLetterId,
      engagementLetterSlug: data.engagementLetterSlug,
      proposalId: data.proposalId,
      proposalSlug: data.proposalSlug,
      currentStage: data.currentStage || 'design-fee',
      stageHistory: data.stageHistory || [],
      designFeeSkipped: data.designFeeSkipped || false,
      inspirationBoardPortalEnabled:
        typeof data.inspirationBoardPortalEnabled === 'boolean'
          ? data.inspirationBoardPortalEnabled
          : undefined,
      modelStepSkipped: data.modelStepSkipped || false,
      conceptApprovalFlowDisabled: !!data.conceptApprovalFlowDisabled,
      designFeePaid: data.designFeePaid || false,
      designFeePaidAt: data.designFeePaidAt?.toDate?.() || undefined,
      amountPaid: data.amountPaid || undefined,
      selectedServices: data.selectedServices || undefined,
      agreementSigned: data.agreementSigned || false,
      agreementSignedAt: data.agreementSignedAt?.toDate?.() || undefined,
      agreementSignedBy: data.agreementSignedBy || undefined,
      agreementSignature: data.agreementSignature || undefined,
      agreementServices: data.agreementServices || undefined,
      agreementTotal: data.agreementTotal || undefined,
      stripeSessionId: data.stripeSessionId || undefined,
      stripeReceiptUrl: data.stripeReceiptUrl || undefined,
      modelUrl: data.modelUrl,
      modelImageUrl: data.modelImageUrl,
      modelLoomUrl: data.modelLoomUrl,
      modelNotes: data.modelNotes,
      plansUrl: data.plansUrl,
      documentUploads: data.documentUploads,
      portalBackgroundImage: data.portalBackgroundImage || undefined,
      portalOverlayColor: data.portalOverlayColor || undefined,
      portalOverlayOpacity: data.portalOverlayOpacity ?? undefined,
      clientUploads: (data.clientUploads || []).map((u: any) => ({
        ...u,
        uploadedAt: u.uploadedAt?.toDate?.() || new Date(u.uploadedAt),
      })),
      createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
      updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt),
      lastViewedAt: data.lastViewedAt?.toDate?.() || undefined,
      questionnaireStatus: data.questionnaireStatus || 'not-started',
    };
    
    // Guests cannot list engagementLetters — resolve via callable (slug or portalId).
    // Stale slug falls back to portal-bound lead lookup.
    if (portalData.leadId || portalData.engagementLetterSlug || portalData.engagementLetterId) {
      try {
        let letter = null as Awaited<ReturnType<typeof getEngagementLetterForPortal>> | null;
        if (portalData.engagementLetterSlug) {
          try {
            letter = await getEngagementLetterBySlug(portalData.engagementLetterSlug, {
              markViewed: false,
              portalId: id,
            });
          } catch {
            letter = await getEngagementLetterForPortal(id);
          }
        } else {
          letter = await getEngagementLetterForPortal(id);
        }
        if (letter?.slug) {
          portalData.engagementLetterSlug = letter.slug;
          portalData.engagementLetterId = letter.id;
        }
        if (letter && (letter.letterType === 'concept' || letter.letterType === 'retainer')) {
          setEngagementLetterType(letter.letterType);
        }
      } catch (err) {
        console.warn('Could not look up engagement letter:', err);
      }
    }

    // Restore persisted service selections (signed state is synced via effectivePortal effect)

    setPortal(portalData);
    setPortalId(id);
    
    try {
      await updateDoc(doc(db, 'clientPortals', id), {
        lastViewedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.warn('Could not update lastViewedAt:', err);
    }
  };

  // Live sync — Project Hub releases update projects.{dealId}.modelUrl after initial load.
  useEffect(() => {
    if (!portal?.id) return;
    const portalId = portal.id;
    const portalRef = doc(db, 'clientPortals', portalId);
    return onSnapshot(
      portalRef,
      snap => {
        const data = snap.data();
        if (!data) return;
        void (async () => {
          const rawProjects: Record<string, PortalProject> = {};
          if (data.projects && typeof data.projects === 'object') {
            for (const [dId, pData] of Object.entries(data.projects as Record<string, unknown>)) {
              if (!pData || typeof pData !== 'object') continue;
              rawProjects[dId] = parsePortalProjectRow(dId, pData as Record<string, unknown>);
            }
          }
          let projects = await reconcilePortalProjectsFromLead(
            rawProjects,
            data.leadId as string | undefined,
          );
          const diff = diffPortalProjectReconciliation(rawProjects, projects);
          if (diff.staleIds.length > 0 || diff.missingIds.length > 0) {
            projects = await reconcileAndPersistPortalProjects(
              portalId,
              rawProjects,
              data.leadId as string | undefined,
            );
          }
          setProjectsList(sortVisiblePortalProjects(projects));
          setPortal(prev => {
            if (!prev) return prev;
            const merged = mergePortalFromFirestore(prev, data as Record<string, unknown>);
            return { ...merged, projects };
          });
        })();
      },
      err => console.warn('[Portal] live sync failed', err),
    );
  }, [portal?.id]);

  // Effective portal: per-project view — never bleed root portal stage/assets onto sibling projects.
  const effectivePortal = useMemo(() => {
    if (!portal) return null;
    if (!selectedProjectId) return portal;
    return buildEffectivePortalProject(portal, selectedProjectId);
  }, [portal, selectedProjectId]);

  const modelWalkthroughEnabled = useMemo(() => {
    if (!portal || !selectedProjectId) return false;
    const proj = portal.projects?.[selectedProjectId];
    return isModelWalkthroughEnabled({
      effectiveDenormalized: proj?.modelWalkthroughEffective,
      projectOverride: proj?.modelWalkthroughEnabled,
    });
  }, [portal, selectedProjectId]);

  /** Parsed labels + index when client has multiple projects and one is selected */
  const multiProjectContext = useMemo(() => {
    if (projectsList.length <= 1 || !selectedProjectId) return null;
    const idx = projectsList.findIndex(p => p.dealId === selectedProjectId);
    if (idx < 0) return null;
    const proj = projectsList[idx];
    const parsed = getPortalProjectDisplay(proj, portal?.clientCompany);
    return {
      indexOneBased: idx + 1,
      total: projectsList.length,
      parsed,
    };
  }, [projectsList, selectedProjectId, portal?.clientCompany]);

  // Calculate progress — count completed + skipped steps
  const progressStageOrder: PortalStage[] = [
    'design-fee', '3d-model', 'quote', 'quote-signed', '2d-plans', 'in-production', 'delivery'
  ];
  const progressPercent = useMemo(() => {
    if (!effectivePortal) return 0;
    const ep = effectivePortal;
    const totalItems = TIMELINE_STEPS.length;
    let completed = 0;

    const currentIdx = progressStageOrder.indexOf(ep.currentStage);
    for (let i = 0; i < TIMELINE_STEPS.length; i++) {
      const step = TIMELINE_STEPS[i];
      const stepIdx = progressStageOrder.indexOf(step.id);
      if (stepIdx < currentIdx) {
        completed++;
      } else if (step.id === 'design-fee' && (ep.designFeePaid || ep.designFeeSkipped)) {
        completed++;
      } else if (step.id === '3d-model' && ep.modelStepSkipped) {
        completed++;
      }
    }

    return Math.min(100, Math.round((completed / Math.max(1, totalItems - 1)) * 100));
  }, [effectivePortal]);

  // Sync local agreement/selection state when effective portal changes (project switch)
  useEffect(() => {
    if (!effectivePortal) return;
    setEngagementSigned(effectivePortal.agreementSigned || false);
    setAwaitingPayment(false);
    paymentUnsubRef.current?.();
    paymentUnsubRef.current = null;
    const svcIds = effectivePortal.selectedServices;
    if (svcIds?.length) {
      setSelectedServices(svcIds);
      setShowServiceSelection(true);
    } else {
      setSelectedServices(['3d-model']);
      setShowServiceSelection(false);
    }
    // Use per-project custom fee services when available, otherwise revert to globals
    const proj = selectedProjectId && portal?.projects?.[selectedProjectId];
    if (proj && proj.customDesignFeeServices?.length) {
      setDesignFeeServicesRaw(proj.customDesignFeeServices);
    } else {
      setDesignFeeServicesRaw(globalFeeServicesRef.current);
    }
  }, [effectivePortal?.agreementSigned, effectivePortal?.selectedServices, selectedProjectId]);

  // Resolve timeline phases: per-portal overrides > global defaults > hardcoded defaults
  const resolvedTimelinePhases = useMemo((): TimelinePhase[] | undefined => {
    const activeProject = selectedProjectId && portal?.projects?.[selectedProjectId]
      ? portal.projects[selectedProjectId]
      : null;
    const overrides = activeProject?.timelineOverrides;
    if (overrides && overrides.length > 0) return overrides as TimelinePhase[];
    if (timelineDefaults && timelineDefaults.length > 0) return timelineDefaults as TimelinePhase[];
    return undefined;
  }, [portal, selectedProjectId, timelineDefaults]);

  // Design-fee timeline: prepend a "3D Model" phase to the standard phases
  const designFeeTimelinePhases = useMemo((): TimelinePhase[] => {
    const modelPhase: TimelinePhase = { id: '3d-model', label: '3D Model', durationWeeks: 2, bufferWeeks: 1 };
    const base = resolvedTimelinePhases ?? [
      { id: '2d-plans', label: 'Engineered Drawings', durationWeeks: 3, bufferWeeks: 1 },
      { id: 'in-production', label: 'Production', durationWeeks: 8, bufferWeeks: 1,
        subPhases: [
          { id: 'material-lead', label: 'Material Lead Time', durationWeeks: 6 },
          { id: 'cnc-production', label: 'CNC Production', durationWeeks: 2 },
        ],
      },
      { id: 'delivery', label: 'Delivery & Install', durationWeeks: 2, bufferWeeks: 0 },
    ];
    return [modelPhase, ...base];
  }, [resolvedTimelinePhases]);

  const designFeeMilestones: TimelineMilestone[] = useMemo(() => [
    { label: 'Design Fee — Select Options', phaseId: '3d-model', anchor: 'start' },
    { label: '50% Deposit', phaseId: '3d-model', anchor: 'end' },
    { label: '40% — Before shipment', phaseId: 'in-production', anchor: 'end' },
    { label: '10% — After install', phaseId: 'delivery', anchor: 'end' },
  ], []);

  // Client's saved target completion date (for reverse timeline mode)
  const savedTargetDate = useMemo((): Date | null => {
    const activeProject = selectedProjectId && portal?.projects?.[selectedProjectId]
      ? portal.projects[selectedProjectId]
      : null;
    return activeProject?.targetCompletionDate || null;
  }, [portal, selectedProjectId]);

  const handleTargetDateChange = async (date: Date | null) => {
    if (isStakeholderViewer) return;
    if (!portal?.id || !selectedProjectId) return;
    try {
      const portalRef = doc(db, 'clientPortals', portal.id);
      await updateDoc(portalRef, {
        [`projects.${selectedProjectId}.targetCompletionDate`]: date ? Timestamp.fromDate(date) : null,
        updatedAt: Timestamp.now(),
      });
    } catch (err) {
      console.error('Error saving target completion date:', err);
    }
  };

  /**
   * Released portal files (Project Hub -> Portal). Subscribed per (portal, project)
   * so the version-history popover under the 3D Model and Engineered Drawings step
   * actions only loads relevant rows. Empty / single-release lists keep the trigger
   * hidden — see `PortalReleasedVersionsPopover` (no trigger when length <= 1).
   */
  const [portalReleases, setPortalReleases] = useState<PortalFileRelease[]>([]);
  const [portalBundles, setPortalBundles] = useState<PortalReleaseBundle[]>([]);
  const [pdfViewerRelease, setPdfViewerRelease] = useState<PortalFileRelease | null>(null);
  const [pdfViewerApproval, setPdfViewerApproval] = useState<PortalDeliverableApproval | null>(null);
  const [viewerAwaitingItem, setViewerAwaitingItem] = useState<{
    deliverableType: 'plans' | 'model' | 'concept';
    releases: PortalFileRelease[];
    bundleId?: string;
    releaseVersionIndex?: number;
    releaseId?: string;
    label: string;
    sharedModelNote?: string;
  } | null>(null);
  const initialsFromViewerRef = useRef(false);
  const [htmlViewer, setHtmlViewer] = useState<{
    url: string;
    title: string;
    approval: PortalDeliverableApproval | null;
    sharedModelNote?: string;
  } | null>(null);
  const [reviewQueue, setReviewQueue] = useState<PortalFileRelease[] | null>(null);
  const [reviewIndex, setReviewIndex] = useState(0);
  const [pdfMarkupTourOnOpen, setPdfMarkupTourOnOpen] = useState(false);
  const signedApproval = (approval?: PortalDeliverableApproval | null) =>
    approval?.status === 'approved' ? approval : null;
  const siblingReleases = (release: PortalFileRelease): PortalFileRelease[] => {
    const id = release.id;
    if (!id) return [release];
    const bundle =
      portalBundles.find(item => item.id && item.id === release.bundleId) ||
      portalBundles.find(item => item.releaseIds.includes(id));
    if (!bundle) return [release];
    const members = bundle.releaseIds
      .map(releaseId => portalReleases.find(item => item.id === releaseId))
      .filter((item): item is PortalFileRelease => !!item && !!item.releasedUrl);
    return members.length > 1 ? members : [release];
  };
  const closeFileViewers = () => {
    setPdfViewerRelease(null);
    setPdfViewerApproval(null);
    setPdfMarkupTourOnOpen(false);
    setHtmlViewer(null);
    setReviewQueue(null);
    setReviewIndex(0);
    setViewerAwaitingItem(null);
    initialsFromViewerRef.current = false;
  };
  const openReleaseFile = (
    release: PortalFileRelease,
    approval?: PortalDeliverableApproval | null,
    queue?: PortalFileRelease[],
    sharedModelNote?: string,
  ) => {
    setViewerAwaitingItem(null);
    const files = (queue && queue.length > 0 ? queue : siblingReleases(release)).filter(item => !!item.releasedUrl);
    const start = Math.max(0, files.findIndex(item => item.id && item.id === release.id));
    const current = files[start] ?? release;
    setReviewQueue(files.length > 1 ? files : null);
    setReviewIndex(files.length > 1 ? start : 0);
    const signed = signedApproval(approval) || signedApproval(current.approval);
    if (current.streamKind === 'three_d_html') {
      setPdfViewerRelease(null);
      setPdfViewerApproval(null);
      setHtmlViewer({
        url: portalHostedAssetHref(current.releasedUrl),
        title: current.displayLabel || current.name || '3D Model',
        approval: signed,
        sharedModelNote,
      });
      return;
    }
    setHtmlViewer(null);
    setPdfViewerRelease(current);
    setPdfViewerApproval(signed);
  };
  const openApprovedPdf = (
    release: PortalFileRelease,
    approval?: PortalDeliverableApproval | null,
    queue?: PortalFileRelease[],
  ) => {
    openReleaseFile(release, approval, queue);
  };
  const advanceReviewFile = () => {
    if (!reviewQueue) return;
    const next = reviewQueue[reviewIndex + 1];
    if (!next) return;
    const pending = viewerAwaitingItem;
    setPdfMarkupTourOnOpen(false);
    openReleaseFile(next, signedApproval(next.approval), reviewQueue);
    if (pending) setViewerAwaitingItem(pending);
  };
  const canAdvanceReview = !!reviewQueue && reviewIndex < reviewQueue.length - 1;
  const [initialsApprovalOpen, setInitialsApprovalOpen] = useState(false);
  const [approvalModal, setApprovalModal] = useState<{
    type: 'plans' | 'model' | 'concept';
    releaseId?: string;
    bundleId?: string;
    releaseVersionIndex?: number;
    /** Bundle members — stamped per release for the approval trail. */
    releaseIds?: string[];
    /** Label override when opened from the approval trail. */
    label?: string;
    sharedModelNote?: string;
  } | null>(null);
  const [designApprovalWalkthroughOpen, setDesignApprovalWalkthroughOpen] = useState(false);
  const [drawingsWalkthrough, setDrawingsWalkthrough] = useState<{
    releases: PortalFileRelease[];
    bundleId?: string;
    releaseVersionIndex?: number;
  } | null>(null);
  // True on the last file of an open drawings approval, while that file is in the viewer.
  // A one-file request has no review queue — it is already the last file.
  const initialsApprovalReady =
    !!drawingsWalkthrough &&
    drawingsWalkthrough.releases.length > 0 &&
    (!reviewQueue || reviewIndex >= reviewQueue.length - 1) &&
    (!!pdfViewerRelease || !!htmlViewer);
  const openDrawingsWalkthrough = (
    releases: PortalFileRelease[],
    bundleId?: string,
    releaseVersionIndex?: number,
  ) => {
    const files = releases.filter(release => !!release.releasedUrl);
    if (files.length === 0 || !portal?.id || !selectedProjectId) return;
    logPortalActivity(
      portal.id,
      selectedProjectId,
      'action',
      'opened_drawings_approval_walkthrough',
      {
        releaseIds: files.map(release => release.id).filter(Boolean),
        bundleId,
        releaseVersionIndex,
      },
      activitySource,
    );
    setDrawingsWalkthrough({ releases: files, bundleId, releaseVersionIndex });
  };
  const canApproveOpenFile =
    !isSampleGcPortal &&
    !isStakeholderViewer &&
    !canAdvanceReview &&
    (initialsApprovalReady || !!viewerAwaitingItem);
  const approveFromOpenFile = () => {
    if (isSampleGcPortal || isStakeholderViewer) return;
    if (initialsApprovalReady) {
      setInitialsApprovalOpen(true);
      return;
    }
    const pending = viewerAwaitingItem;
    if (!pending) return;
    if (pending.deliverableType === 'plans') {
      initialsFromViewerRef.current = true;
      openDrawingsWalkthrough(pending.releases, pending.bundleId, pending.releaseVersionIndex);
      setInitialsApprovalOpen(true);
      return;
    }
    setApprovalModal({
      type: pending.deliverableType,
      releaseId: pending.releaseId,
      bundleId: pending.bundleId,
      releaseVersionIndex: pending.releaseVersionIndex,
      releaseIds: pending.releases.map(release => release.id || '').filter(Boolean),
      label: pending.label,
      sharedModelNote: pending.sharedModelNote,
    });
  };
  const rememberDeliverableApproval = (recorded: {
    type: 'plans' | 'model' | 'concept';
    releaseId?: string;
    bundleId?: string;
    releaseVersionIndex?: number;
  }) => {
    setPortal(prev => {
      if (!prev || !selectedProjectId) return prev;
      const nextApproval = {
        status: 'approved' as const,
        approvedAt: new Date(),
        approvedByName: user?.name || user?.email || 'Client',
        signature: '',
        releaseId: recorded.releaseId,
        bundleId: recorded.bundleId,
        releaseVersionIndex: recorded.releaseVersionIndex,
      };
      const project = prev.projects?.[selectedProjectId];
      const existingApproval =
        recorded.type === 'plans'
          ? project?.plansApproval ?? prev.plansApproval
          : recorded.type === 'concept'
            ? project?.conceptApproval ?? prev.conceptApproval
            : project?.modelApproval ?? prev.modelApproval;
      const existingHistory =
        recorded.type === 'plans'
          ? project?.plansApprovalHistory ?? prev.plansApprovalHistory
          : recorded.type === 'concept'
            ? project?.conceptApprovalHistory ?? prev.conceptApprovalHistory
            : project?.modelApprovalHistory ?? prev.modelApprovalHistory;
      const archived = nextApprovalWithHistory(existingApproval, existingHistory, nextApproval);
      const projectPatch =
        recorded.type === 'plans'
          ? { plansApproval: archived.current, plansApprovalHistory: archived.history }
          : recorded.type === 'concept'
            ? { conceptApproval: archived.current, conceptApprovalHistory: archived.history }
            : { modelApproval: archived.current, modelApprovalHistory: archived.history };
      const updated: ClientPortal = { ...prev };
      if (updated.projects?.[selectedProjectId]) {
        updated.projects = {
          ...updated.projects,
          [selectedProjectId]: {
            ...updated.projects[selectedProjectId],
            ...projectPatch,
          },
        };
      }
      if (prev.dealId === selectedProjectId || !isMultiProjectPortal(prev)) {
        Object.assign(updated, projectPatch);
      }
      return updated;
    });
  };
  const [isDesktopPortal, setIsDesktopPortal] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = () => setIsDesktopPortal(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  useEffect(() => {
    setPortalReleases([]);
    setPortalBundles([]);
    if (!portal?.id || !selectedProjectId) return;
    const unsubReleases = subscribePortalReleasesForDeal(portal.id, selectedProjectId, setPortalReleases);
    const unsubBundles = subscribePortalBundlesForDeal(portal.id, selectedProjectId, setPortalBundles);
    return () => {
      unsubReleases();
      unsubBundles();
    };
  }, [portal?.id, selectedProjectId]);

  /** Same trail input path as staff Client view — portal mirror only, never hub streams. */
  const portalTrailInput = useMemo(() => {
    if (!selectedProjectId || !portal) return null;
    const row = portal.projects?.[selectedProjectId];
    const projectForTrail: PortalProject | null = row
      ? row
      : portal.dealId === selectedProjectId
        ? parsePortalProjectRow(selectedProjectId, portal as unknown as Record<string, unknown>)
        : null;
    return buildClientPortalTrailInput({
      releases: portalReleases,
      bundles: portalBundles,
      portalProject: projectForTrail,
      legacyApprovals: effectivePortal ? groupPortalDealApprovals(effectivePortal) : undefined,
      proposalApprovedAt,
      proposalApprovedByName,
    });
  }, [
    portal,
    selectedProjectId,
    portalReleases,
    portalBundles,
    effectivePortal,
    proposalApprovedAt,
    proposalApprovedByName,
  ]);

  const releasedPlans = useMemo(
    () =>
      portalReleases.filter(
        r =>
          r.streamKind === 'two_d_pdf' &&
          r.dealId === selectedProjectId &&
          (!r.portalTarget || r.portalTarget === 'engineered_drawings'),
      ),
    [portalReleases, selectedProjectId],
  );
  const releasedConceptElevations = useMemo(
    () =>
      portalReleases.filter(
        r =>
          r.streamKind === 'two_d_pdf' &&
          r.dealId === selectedProjectId &&
          r.portalTarget === 'concept_elevations',
      ),
    [portalReleases, selectedProjectId],
  );
  const releasedModels = useMemo(
    () => portalReleases.filter(
      r => r.streamKind === 'three_d_html' && r.dealId === selectedProjectId,
    ),
    [portalReleases, selectedProjectId],
  );

  const planBundles = useMemo(
    () =>
      mergeBundlesWithReleases(
        portalBundles.filter(
          b =>
            b.streamKind === 'two_d_pdf' &&
            b.dealId === selectedProjectId &&
            bundlePortalTarget(b) === 'engineered_drawings',
        ),
        releasedPlans,
      ),
    [portalBundles, releasedPlans, selectedProjectId],
  );
  const conceptBundles = useMemo(
    () =>
      mergeBundlesWithReleases(
        portalBundles.filter(
          b =>
            b.streamKind === 'two_d_pdf' &&
            b.dealId === selectedProjectId &&
            bundlePortalTarget(b) === 'concept_elevations',
        ),
        releasedConceptElevations,
      ),
    [portalBundles, releasedConceptElevations, selectedProjectId],
  );
  const modelBundles = useMemo(
    () =>
      mergeBundlesWithReleases(
        portalBundles.filter(b => b.streamKind === 'three_d_html' && b.dealId === selectedProjectId),
        releasedModels,
      ),
    [portalBundles, releasedModels, selectedProjectId],
  );

  const currentPlanBundle = planBundles[0];
  const currentConceptBundle = conceptBundles[0];
  const currentModelBundle = modelBundles[0];
  const primaryPlanRelease = useMemo(() => {
    const latest = latestPortalRelease(releasedPlans);
    if (!currentPlanBundle) return latest;
    return (
      releasedPlans.find(r => r.id === currentPlanBundle.primaryReleaseId) ||
      latest
    );
  }, [currentPlanBundle, releasedPlans]);

  const primaryModelRelease = useMemo(() => {
    const latest = latestPortalRelease(releasedModels);
    if (!currentModelBundle) return latest;
    return (
      releasedModels.find(r => r.id === currentModelBundle.primaryReleaseId) ||
      latest
    );
  }, [currentModelBundle, releasedModels]);

  const primaryConceptRelease = useMemo(() => {
    const latest = latestPortalRelease(releasedConceptElevations);
    if (!currentConceptBundle) return latest;
    return (
      releasedConceptElevations.find(r => r.id === currentConceptBundle.primaryReleaseId) ||
      latest
    );
  }, [currentConceptBundle, releasedConceptElevations]);

  const modelPhase = (release: PortalFileRelease) => {
    const bundle = modelBundles.find(b => !!release.id && b.releaseIds.includes(release.id));
    if (bundle?.designPhase === 'concept' || bundle?.designPhase === 'engineering') return bundle.designPhase;
    return portalReleaseDesignPhase(release);
  };
  const conceptPhaseModels = useMemo(
    () => releasedModels.filter(r => modelPhase(r) === 'concept'),
    [releasedModels, modelBundles],
  );
  const engineeringPhaseModels = useMemo(
    () => releasedModels.filter(r => modelPhase(r) === 'engineering'),
    [releasedModels, modelBundles],
  );
  const currentConceptModelBundle = useMemo(
    () =>
      modelBundles.find(b =>
        conceptPhaseModels.some(r => !!r.id && b.releaseIds.includes(r.id)),
      ),
    [modelBundles, conceptPhaseModels],
  );
  const conceptPhaseModelBundles = useMemo(() => {
    const ids = new Set(conceptPhaseModels.map(r => r.id).filter((id): id is string => !!id));
    return modelBundles.filter(b => b.releaseIds.some(id => ids.has(id)));
  }, [modelBundles, conceptPhaseModels]);
  const modelPackageReleases = useMemo(
    () => currentPackageReleases(currentConceptModelBundle, conceptPhaseModels),
    [currentConceptModelBundle, conceptPhaseModels],
  );
  const primaryConceptModelRelease = useMemo(() => {
    const latest = latestPortalRelease(conceptPhaseModels);
    if (!currentConceptModelBundle) return latest;
    return (
      conceptPhaseModels.find(r => r.id === currentConceptModelBundle.primaryReleaseId) || latest
    );
  }, [currentConceptModelBundle, conceptPhaseModels]);
  const conceptPackageReleases = useMemo(
    () => currentPackageReleases(currentConceptBundle, releasedConceptElevations),
    [currentConceptBundle, releasedConceptElevations],
  );
  const engineeringPhaseModelBundles = useMemo(() => {
    const ids = new Set(engineeringPhaseModels.map(r => r.id).filter((id): id is string => !!id));
    return modelBundles.filter(b => b.releaseIds.some(id => ids.has(id)));
  }, [modelBundles, engineeringPhaseModels]);
  const currentEngineeringModelBundle = engineeringPhaseModelBundles[0];
  const engineeringModelPackageReleases = useMemo(
    () => currentPackageReleases(currentEngineeringModelBundle, engineeringPhaseModels),
    [currentEngineeringModelBundle, engineeringPhaseModels],
  );
  const planPackageReleases = useMemo(
    () => currentPackageReleases(currentPlanBundle, releasedPlans),
    [currentPlanBundle, releasedPlans],
  );
  const engineeredTableReady =
    planPackageReleases.length > 0 || engineeringModelPackageReleases.length > 0;
  const conceptStructureNeedsApproval = useMemo(() => {
    const rows = [...conceptPhaseModels, ...releasedConceptElevations];
    return rows.some(
      r =>
        (r.structureIds?.length ?? 0) > 0 &&
        r.approvalRequested === true &&
        r.approval?.status !== 'approved',
    );
  }, [conceptPhaseModels, releasedConceptElevations]);
  const conceptSectionCollapsible =
    isConceptReviewRetired({
      proposalStatus,
      currentStage: effectivePortal?.currentStage,
    }) && !conceptStructureNeedsApproval;
  const [conceptDeliverablesToggled, setConceptDeliverablesToggled] = useState<boolean | null>(null);
  useEffect(() => {
    setConceptDeliverablesToggled(null);
  }, [selectedProjectId]);
  const conceptDeliverablesOpen = conceptSectionCollapsible
    ? conceptDeliverablesToggled ?? false
    : true;
  const approvalForOpenedRelease = (
    release: PortalFileRelease,
    explicit?: PortalDeliverableApproval | null,
  ): PortalDeliverableApproval | null => {
    if (explicit?.status === 'approved') return explicit;
    if (release.approval?.status === 'approved') return release.approval;
    if (!proposalApprovedAt) return null;
    const covered = conceptPhaseModels
      .filter(r => r.releasedAt instanceof Date && r.releasedAt.getTime() <= proposalApprovedAt.getTime())
      .sort((a, b) => b.releasedAt.getTime() - a.releasedAt.getTime());
    if (covered[0]?.id !== release.id) return null;
    return {
      status: 'approved',
      approvedByName: proposalApprovedByName || '',
      approvedAt: proposalApprovedAt,
      signature: '',
    };
  };

  const latestReleasedModelUrl = useMemo(
    () => latestPortalRelease(releasedModels)?.releasedUrl,
    [releasedModels],
  );

  const showDeliverableLatestColumn = useMemo(
    () => modelBundles.length > 1 || conceptBundles.length > 1,
    [modelBundles.length, conceptBundles.length],
  );

  const openConceptPdfMarkup = useCallback(
    (context: 'deliverables_table' | 'design_approval_walkthrough') => {
      if (!primaryConceptRelease || !portal?.id) return;
      logPortalActivity(
        portal.id,
        selectedProjectId,
        'link_click',
        'opened_portal_pdf_markup',
        {
          releaseId: primaryConceptRelease.id,
          context,
        },
        activitySource,
      );
      openApprovedPdf(primaryConceptRelease);
    },
    [primaryConceptRelease, portal?.id, selectedProjectId, activitySource],
  );

  const pdfViewerBundleId = useMemo(() => {
    if (!pdfViewerRelease?.id) return undefined;
    return (
      resolvePortalReleaseBundleId(pdfViewerRelease.id, conceptBundles) ||
      resolvePortalReleaseBundleId(pdfViewerRelease.id, planBundles)
    );
  }, [pdfViewerRelease?.id, conceptBundles, planBundles]);

  const proposalSigningGate = useMemo(() => {
    if (!effectivePortal) {
      return evaluateProposalSigningGate({ isRomPricing: proposalIsRom });
    }
    const input = {
      modelUrl: latestReleasedModelUrl || effectivePortal.modelUrl,
      modelImageUrl: effectivePortal.modelImageUrl,
      conceptElevationUrl:
        primaryConceptRelease?.releasedUrl || effectivePortal.conceptElevationUrl,
      modelStepSkipped: effectivePortal.modelStepSkipped,
      conceptApprovalFlowDisabled: effectivePortal.conceptApprovalFlowDisabled,
      skipConceptApproval: shouldSkipConceptApprovalForSigning({
        flowDisabled: effectivePortal.conceptApprovalFlowDisabled,
        proposalStatus,
      }),
      modelApproval: effectivePortal.modelApproval,
      modelApprovalHistory: effectivePortal.modelApprovalHistory,
      conceptApproval: effectivePortal.conceptApproval,
      conceptApprovalHistory: effectivePortal.conceptApprovalHistory,
      modelReleaseId: primaryModelRelease?.id,
      modelReleaseVersionIndex: primaryModelRelease?.releaseVersionIndex,
      conceptReleaseId: primaryConceptRelease?.id,
      conceptReleaseVersionIndex: primaryConceptRelease?.releaseVersionIndex,
      isRomPricing: proposalIsRom,
    };
    return evaluateProposalSigningGate(input);
  }, [effectivePortal, primaryModelRelease, primaryConceptRelease, proposalIsRom, latestReleasedModelUrl, proposalStatus]);

  useEffect(() => {
    if (requiresPin || loading || !portal) return;
    if (searchParams.get('focus') !== PORTAL_FOCUS_CONCEPT_MODEL) return;
    const t = window.setTimeout(() => {
      document.getElementById('cps-step-3d-model')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const conceptApprovalFlowOn = isConceptApprovalFlowEnabled({
        flowDisabled: effectivePortal?.conceptApprovalFlowDisabled,
        proposalStatus,
      });
      if (
        !isSampleGcPortal &&
        !isStakeholderViewer &&
        !proposalIsRom &&
        conceptApprovalFlowOn &&
        !isConceptReviewRetired({
          proposalStatus,
          currentStage: effectivePortal?.currentStage,
        }) &&
        proposalSigningGate.reasons.includes('deliverables_pending')
      ) {
        setDesignApprovalWalkthroughOpen(true);
      }
      const cleaned = new URLSearchParams(searchParams);
      cleaned.delete('focus');
      setSearchParams(cleaned, { replace: true });
    }, 250);
    return () => window.clearTimeout(t);
  }, [
    requiresPin,
    loading,
    portal,
    searchParams,
    isSampleGcPortal,
    isStakeholderViewer,
    proposalIsRom,
    proposalSigningGate.reasons,
    effectivePortal?.conceptApprovalFlowDisabled,
    effectivePortal?.currentStage,
    proposalStatus,
    setSearchParams,
  ]);

  const [supplementarySections, setSupplementarySections] = useState<PortalSupplementarySection[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedProjectId || portalReleases.length === 0) {
        if (!cancelled) setSupplementarySections([]);
        return;
      }
      const dealReleases = portalReleases.filter(r => r.dealId === selectedProjectId);
      const dealBundles = portalBundles.filter(b => b.dealId === selectedProjectId);
      const sections = await loadSupplementaryStreamSections(dealReleases, dealBundles);
      if (!cancelled) setSupplementarySections(sections);
    })();
    return () => {
      cancelled = true;
    };
  }, [portalReleases, portalBundles, selectedProjectId]);

  const [signedProposalBase, setSignedProposalBase] = useState<number | null>(null);
  /** Phased projects: full proposal doc for the phase summary card (null when not phased). */
  const [phasedProposalDoc, setPhasedProposalDoc] = useState<ProposalBuilderData | null>(null);
  useEffect(() => {
    if (!selectedProjectId) {
      setSignedProposalBase(null);
      setPhasedProposalDoc(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await ensureAuth();
      } catch {
        /* handled by snapshot error below */
      }
    })();
    // Live subscription so offered/committed phase changes appear without a refresh.
    const unsub = onSnapshot(
      doc(db, 'proposalBuilders', selectedProjectId),
      snap => {
        if (cancelled) return;
        if (snap.exists()) {
          const data = snap.data() as ProposalBuilderData;
          setSignedProposalBase(getSignedCommercialListTotal(data));
          setPhasedProposalDoc(isPhasedProposal(data) ? data : null);
        } else {
          setSignedProposalBase(null);
          setPhasedProposalDoc(null);
        }
      },
      () => {
        if (!cancelled) {
          setSignedProposalBase(null);
          setPhasedProposalDoc(null);
        }
      },
    );
    return () => {
      cancelled = true;
      unsub();
    };
  }, [selectedProjectId]);

  /** Timeline/display portal — per-project stage + release/proposal bumps for client UI. */
  const timelinePortal = useMemo((): ClientPortal | null => {
    if (!effectivePortal) return null;
    const latestModel = latestPortalRelease(releasedModels);
    const latestConcept = latestPortalRelease(releasedConceptElevations);
    const enriched = enrichPortalWithReleases(effectivePortal, {
      releasedModelUrl: latestModel?.releasedUrl,
      releaseDealId: selectedProjectId,
      proposalStatus,
    });
    return {
      ...enriched,
      modelUrl: latestModel?.releasedUrl || enriched.modelUrl,
      conceptElevationUrl: latestConcept?.releasedUrl || enriched.conceptElevationUrl,
    };
  }, [effectivePortal, releasedModels, releasedConceptElevations, proposalStatus, selectedProjectId]);

  // Load proposal slug + status ONLY from proposalBuilders/{dealId}.
  // Portal row slugs can be stale or copied from a sibling deal (e.g. aux cabin inheriting
  // admin slug) — never use them to bump stages or show a proposal link without a builder doc.
  useEffect(() => {
    if (!selectedProjectId) {
      setProposalStatus(null);
      setProposalApprovedAt(null);
      setProposalApprovedByName(null);
      setProposalIsRom(false);
      setResolvedProposalSlug(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await ensureAuth();
        const snap = await getDoc(doc(db, 'proposalBuilders', selectedProjectId));
        if (cancelled) return;
        if (!snap.exists()) {
          setProposalStatus(null);
          setProposalApprovedAt(null);
          setProposalApprovedByName(null);
          setProposalIsRom(false);
          setResolvedProposalSlug(null);
          return;
        }
        const data = snap.data();
        setProposalStatus((data.status as string) || 'draft');
        const signedRaw = data.approvedAt ?? data.signature?.signedAt;
        const signedAt =
          signedRaw && typeof signedRaw.toDate === 'function'
            ? signedRaw.toDate()
            : signedRaw
              ? new Date(signedRaw)
              : null;
        const signedBy = typeof data.signature?.signedBy === 'string' ? data.signature.signedBy.trim() : '';
        setProposalApprovedAt(
          data.status === 'approved' && signedAt instanceof Date && !Number.isNaN(signedAt.getTime())
            ? signedAt
            : null,
        );
        setProposalApprovedByName(data.status === 'approved' && signedBy ? signedBy : null);
        setProposalIsRom(!!data.isRomPricing);
        setResolvedProposalSlug(typeof data.slug === 'string' ? data.slug : null);
      } catch (err) {
        console.error('Error loading proposal for portal project:', err);
        if (!cancelled) {
          setProposalStatus(null);
          setProposalApprovedAt(null);
          setProposalApprovedByName(null);
          setProposalIsRom(false);
          setResolvedProposalSlug(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId]);

  // Slug for client-facing proposal links — builder doc only (see effect above).
  const proposalSlugForDisplay = resolvedProposalSlug;

  const proposalActuallySigned = isPortalProposalSigned(proposalStatus);
  const canOfferSignProposal = canOfferPortalSignProposal({
    canSign: proposalSigningGate.canSign,
    proposalVisible: isProposalVisibleToClient(proposalStatus),
    hasProposalSlug: !!proposalSlugForDisplay,
    isStakeholderViewer,
    isSampleGcPortal,
    alreadySigned: proposalActuallySigned,
  });

  const resolveStepStatus = useCallback(
    (step: TimelineStep, portal: ClientPortal): StepStatus => {
      const statuses = computePortalStepStatuses(
        signalsFromPortalProject(portal, {
          proposalStatus,
          releasedModelUrl: latestReleasedModelUrl,
        }),
      );
      const status = statuses[step.id];
      if (
        step.id === '3d-model' &&
        status === 'active' &&
        portal.currentStage === '3d-model' &&
        !portal.designFeePaid &&
        !portal.designFeeSkipped &&
        !portalHasModelContent(portal) &&
        !latestReleasedModelUrl
      ) {
        return 'locked';
      }
      return status;
    },
    [proposalStatus, latestReleasedModelUrl],
  );

  // Subscribe to change orders for the selected project (dealId === selectedProjectId).
  useEffect(() => {
    if (!selectedProjectId) {
      setChangeOrders([]);
      return;
    }
    let unsub: (() => void) | undefined;
    let cancelled = false;
    (async () => {
      try {
        await ensureAuth();
      } catch {
        /* listener will surface any permission issues */
      }
      if (cancelled) return;
      unsub = subscribeChangeOrdersForDeal(selectedProjectId, setChangeOrders);
    })();
    return () => {
      cancelled = true;
      if (unsub) unsub();
    };
  }, [selectedProjectId]);

  // Client-visible change orders: only those released for signature or already signed.
  const visibleChangeOrders = useMemo(
    () =>
      changeOrders
        .filter(co => co.status === 'sent' || co.status === 'approved')
        .sort((a, b) => (a.number || 0) - (b.number || 0)),
    [changeOrders],
  );

  const allVisibleChangeOrdersApproved = useMemo(
    () => visibleChangeOrders.length > 0 && visibleChangeOrders.every(co => co.status === 'approved'),
    [visibleChangeOrders],
  );

  const currentContractValue = useMemo(() => {
    if (signedProposalBase == null) return null;
    return signedProposalBase + sumApprovedChangeOrders(visibleChangeOrders);
  }, [signedProposalBase, visibleChangeOrders]);

  // Contract payment installments with folded / immediate CO treatment.
  const portalScheduleBreakdown = useMemo(
    () => buildPaymentScheduleBreakdown(signedProposalBase ?? 0, changeOrders, {
      invoicedInstallments: invoicedInstallmentLocks(effectivePortal?.installmentPayments),
    }),
    [signedProposalBase, changeOrders, effectivePortal?.installmentPayments],
  );

  // Surety bond status mirrored from the CRM's bond request (shown at every status, not just issued).
  const portalBond = useMemo(() => {
    const bond = effectivePortal?.bond;
    if (!bond) return null;
    return { bond, copy: portalBondCopy(bond) };
  }, [effectivePortal?.bond]);

  const portalStatementProject = useMemo(() => {
    const project = projectsList.find((item) => item.dealId === selectedProjectId);
    const parsed = getPortalProjectDisplay(project, portal?.clientCompany);
    const clientCompany = (project?.clientLabel || portal?.clientCompany || '').trim();
    return { ...parsed, clientCompany };
  }, [projectsList, selectedProjectId, portal?.clientCompany]);

  // Per-installment paid status mirrored from the sales order (deposit/pre_shipment/final).
  const portalInstallmentStatuses = useMemo(() => {
    const list = effectivePortal?.installmentPayments;
    if (!list?.length) return undefined;
    const map: Record<string, { status: string; paidAt?: Date; invoicedAt?: Date; amountBilled?: number; amountPaid?: number; amountUnbilled?: number; partiallyBilled?: boolean; structureGroupLabels?: string[] }> = {};
    for (const p of list) {
      map[p.installmentId] = {
        status: p.status,
        paidAt: p.paidAt,
        invoicedAt: p.invoicedAt,
        amountBilled: p.amountBilled,
        amountPaid: p.amountPaid,
        amountUnbilled: p.amountUnbilled,
        partiallyBilled: p.partiallyBilled,
        structureGroupLabels: p.structureGroupLabels,
      };
    }
    return map;
  }, [effectivePortal?.installmentPayments]);

  // Fetch engagement letter type via callable (guest list denied).
  useEffect(() => {
    const portalId = portal?.id;
    if (!portalId) return;
    if (engagementLetterType) return;
    const fetchLetterStatus = async () => {
      try {
        let letter = null as Awaited<ReturnType<typeof getEngagementLetterForPortal>> | null;
        if (portal?.engagementLetterSlug) {
          try {
            letter = await getEngagementLetterBySlug(portal.engagementLetterSlug, {
              markViewed: false,
              portalId,
            });
          } catch {
            letter = await getEngagementLetterForPortal(portalId);
          }
        } else {
          letter = await getEngagementLetterForPortal(portalId);
        }
        if (letter && (letter.letterType === 'concept' || letter.letterType === 'retainer')) {
          setEngagementLetterType(letter.letterType);
        }
        if (letter && letter.slug && !portal?.engagementLetterSlug) {
          const resolvedSlug = letter.slug;
          const resolvedId = letter.id;
          setPortal(prev => prev ? {
            ...prev,
            engagementLetterSlug: resolvedSlug || undefined,
            engagementLetterId: resolvedId,
          } : null);
        }
      } catch (err) {
        console.warn('Could not fetch engagement letter status:', err);
      }
    };
    fetchLetterStatus();
  }, [portal?.id, portal?.engagementLetterSlug, engagementLetterType]);

  // Fetch portal notes when portal is loaded
  const portalDocId = portal?.id;
  useEffect(() => {
    if (!portalDocId) return;
    const fetchNotes = async () => {
      try {
        const notesRef = collection(db, 'portalNotes');
        const q = query(notesRef, where('portalId', '==', portalDocId));
        const snap = await getDocs(q);
        const notes: PortalNote[] = snap.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            portalId: data.portalId,
            step: data.step,
            authorType: data.authorType,
            authorName: data.authorName,
            message: data.message,
            createdAt: data.createdAt?.toDate?.() || new Date(),
          };
        });
        notes.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        setPortalNotes(notes);
      } catch (err) {
        console.error('Error fetching portal notes:', err);
      }
    };
    fetchNotes();
  }, [portalDocId]);
  
  // Submit a note for a given step
  const handleSubmitNote = async (step: PortalStage) => {
    const message = (noteInputs[step] || '').trim();
    if (!message || !portal?.id) return;
    logPortalActivity(portal.id, selectedProjectId, 'action', 'sent_note', { step }, activitySource);
    setSubmittingNoteStep(step);
    try {
      const authorType = isStaffPortalUser ? 'employee' : 'client';
      const authorName = isStaffPortalUser
        ? (user?.name || user?.email || 'Team Member')
        : (portal.clientName || 'Client');
      const noteData = {
        portalId: portal.id,
        step,
        authorType: authorType as 'client' | 'employee',
        authorName,
        message,
        createdAt: Timestamp.now(),
      };
      const docRef = await addDoc(collection(db, 'portalNotes'), noteData);
      setPortalNotes(prev => [...prev, {
        id: docRef.id,
        ...noteData,
        createdAt: new Date(),
      }]);
      setNoteInputs(prev => ({ ...prev, [step]: '' }));
      setTimeout(() => notesEndRefs.current[step]?.scrollIntoView({ behavior: 'smooth' }), 100);

      // Notification title per step
      const stepTitles: Record<string, string> = {
        'design-fee': 'Design Fee',
        '3d-model': '3D Model',
        'quote': 'Quote',
        '2d-plans': 'Engineered Drawings',
      };
      const stepLabel = stepTitles[step] || step;

      if (!isStaffPortalUser) {
        try {
          const functionsBase = window.location.hostname === 'localhost'
            ? 'http://localhost:5001/lctf-projects/us-central1'
            : 'https://us-central1-lctf-projects.cloudfunctions.net';
          const truncatedMsg = message.length > 80 ? message.substring(0, 80) + '...' : message;
          await fetch(`${functionsBase}/sendPortalNotification`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'portal_feedback',
              portalId: portal.id,
              leadId: portal.leadId || null,
              clientName: portal.clientName,
              message: `${portal.clientName} left a note on ${stepLabel}: "${truncatedMsg}"`,
            }),
          });
        } catch { /* non-blocking */ }
      }
    } catch (err) {
      console.error('Error submitting note:', err);
    } finally {
      setSubmittingNoteStep(null);
    }
  };

  // Handle client file upload (Step 1)
  const handleClientFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !portal?.id) return;
    logPortalActivity(portal.id, selectedProjectId, 'action', 'uploaded_file', { fileCount: files.length }, activitySource);

    setUploadingFile(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const storagePath = `portal-files/${portal.id}/client-uploads/${fileId}-${file.name}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        return {
          id: fileId,
          name: file.name,
          url,
          type: file.type,
          step: 'design-fee' as PortalStage,
          uploadedAt: new Date(),
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);

      // Save to Firestore
      await updateDoc(doc(db, 'clientPortals', portal.id), {
        clientUploads: arrayUnion(...uploadedFiles.map(f => ({
          ...f,
          uploadedAt: Timestamp.now(),
        }))),
        updatedAt: Timestamp.now(),
      });

      // Update local state
      setPortal(prev => prev ? {
        ...prev,
        clientUploads: [...(prev.clientUploads || []), ...uploadedFiles],
      } : null);

      try {
        const functionsBase = window.location.hostname === 'localhost'
          ? 'http://localhost:5001/lctf-projects/us-central1'
          : 'https://us-central1-lctf-projects.cloudfunctions.net';
        const fileNames = uploadedFiles.map(f => f.name).join(', ');
        await fetch(`${functionsBase}/sendPortalNotification`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'portal_upload',
            portalId: portal.id,
            leadId: portal.leadId || null,
            clientName: portal.clientName,
            message: `${portal.clientName} uploaded files: ${fileNames}`,
          }),
        });
      } catch { /* non-blocking */ }
    } catch (err) {
      console.error('Error uploading files:', err);
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleStartEditName = () => {
    if (!portal) return;
    const parts = (portal.clientName || '').trim().split(/\s+/);
    setEditFirstName(parts[0] || '');
    setEditLastName(parts.slice(1).join(' ') || '');
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    if (!portal || !portal.id || savingName) return;
    const firstName = editFirstName.trim();
    const lastName = editLastName.trim();
    const newClientName = [firstName, lastName].filter(Boolean).join(' ');
    if (!newClientName) return;

    setSavingName(true);
    try {
      await updateDoc(doc(db, 'clientPortals', portal.id), {
        clientName: newClientName,
        updatedAt: Timestamp.now(),
      });
      if (portal.leadId) {
        const leadFirstName = firstName || (lastName ? '' : newClientName);
        const leadLastName = firstName ? lastName : '';
        await updateDoc(doc(db, 'leads', portal.leadId), {
          firstName: leadFirstName || newClientName,
          lastName: leadLastName,
          updatedAt: Timestamp.now(),
        });
      }
      setPortal(prev => prev ? { ...prev, clientName: newClientName } : null);
      setIsEditingName(false);
    } catch (err) {
      console.error('Error saving name:', err);
    } finally {
      setSavingName(false);
    }
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
  };

  // PIN handlers
  const handlePinChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    
    const newPinDigits = [...pinDigits];
    newPinDigits[index] = value.slice(-1);
    setPinDigits(newPinDigits);
    setPinError('');
    
    if (value && index < 3) {
      pinInputRefs.current[index + 1]?.focus();
    }
  };
  
  const handlePinKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !pinDigits[index] && index > 0) {
      pinInputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (pinDigits.every(d => d !== '')) {
        verifyPin();
      }
    }
  };
  
  const handlePinPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pastedData.length === 4) {
      setPinDigits(pastedData.split(''));
      pinInputRefs.current[3]?.focus();
    }
  };
  
  const verifyPin = async () => {
    const enteredPin = pinDigits.join('');
    if (!slug) {
      setPinError('Portal link is missing. Please use your invitation link.');
      return;
    }
    if (enteredPin.length !== 4) {
      setPinError('Please enter all 4 digits');
      return;
    }
    
    setVerifyingPin(true);
    
    try {
      await verifyPortalPinServer(slug, enteredPin);
      sessionStorage.setItem(`portal_verified_${slug}`, 'true');
      sessionStorage.removeItem(`portal_stakeholder_${slug}`);
      sessionStorage.removeItem(`portal_stakeholder_token_${slug}`);
      setStakeholderDisplayName(null);
      setStakeholderCompanyName(null);
      try {
        await clearStakeholderSessionCallable();
      } catch (e) {
        console.warn('[Portal] clearStakeholderSession:', e);
      }
      setRequiresPin(false);

      // Load by portalId from access meta — never slug-query after PIN claim.
      const accessMeta = await getPortalAccessMeta(slug);
      const portalSnap = await getDoc(doc(db, 'clientPortals', accessMeta.portalId));
      if (portalSnap.exists()) {
        await loadPortalData(portalSnap.id, portalSnap.data());
      }
    } catch (err) {
      console.warn('[Portal] verifyPortalPin failed:', err);
      setPinError('Incorrect PIN. Please try again.');
      setPinDigits(['', '', '', '']);
      pinInputRefs.current[0]?.focus();
    } finally {
      setVerifyingPin(false);
    }
  };
  
  // Generate and open signed agreement PDF
  const handleViewSignedAgreement = async () => {
    if (!portal?.id || generatingPdf) return;
    logPortalActivity(portal.id, selectedProjectId, 'link_click', 'viewed_signed_agreement', undefined, activitySource);

    const pdfWindow = window.open('', '_blank');

    setGeneratingPdf(true);
    try {
      const portalSnap = await getDoc(doc(db, 'clientPortals', portal.id));
      const data = portalSnap.data();
      if (!data) {
        pdfWindow?.close();
        setGeneratingPdf(false);
        return;
      }
      const agreement = getPortalAgreementSnapshot(data, selectedProjectId);
      if (!agreement.agreementSigned) {
        pdfWindow?.close();
        setGeneratingPdf(false);
        return;
      }

      const signedAt =
        (agreement.agreementSignedAt as { toDate?: () => Date })?.toDate?.() ||
        (agreement.agreementSignedAt ? new Date(agreement.agreementSignedAt as string | number) : new Date());
      const services = agreement.agreementServices?.length
        ? agreement.agreementServices
        : designFeeServices
            .filter(s => (agreement.selectedServices || selectedServices).includes(s.id))
            .map(s => ({ name: s.name, price: s.price }));

      const projName = selectedProjectId ? portal.projects?.[selectedProjectId]?.projectName : undefined;

      const blob = await generateSignedAgreementPdf({
        clientName: portal.clientName,
        projectName: projName || undefined,
        signedBy: agreement.agreementSignedBy || portal.clientName,
        signedAt,
        signatureDataUrl: agreement.agreementSignature || '',
        services,
        totalAmount: agreement.agreementTotal || services.reduce((sum: number, s: any) => sum + (s.price || 0), 0),
      });

      const url = URL.createObjectURL(blob);
      if (pdfWindow) {
        pdfWindow.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch (err) {
      console.error('Error generating agreement PDF:', err);
      pdfWindow?.close();
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDownloadStripeReceipt = async () => {
    if (!portal?.id || loadingReceipt) return;

    if (effectivePortal?.stripeReceiptUrl) {
      window.open(effectivePortal.stripeReceiptUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (!effectivePortal?.stripeSessionId) return;

    setLoadingReceipt(true);
    try {
      const functionsBase = process.env.REACT_APP_FUNCTIONS_URL
        || `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID || 'lctf-projects'}.cloudfunctions.net`;
      const resp = await fetch(`${functionsBase}/getDesignFeeReceiptUrl`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          portalId: portal.id,
          projectId: selectedProjectId || undefined,
        }),
      });
      const data = await resp.json();
      if (!resp.ok || !data?.url) {
        throw new Error(data?.error || 'Receipt not available');
      }
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error loading Stripe receipt:', err);
      alert('Could not load your payment receipt. Please contact your project manager.');
    } finally {
      setLoadingReceipt(false);
    }
  };

  // Clean up payment listener on unmount
  useEffect(() => {
    return () => { paymentUnsubRef.current?.(); };
  }, []);

  // Direct-to-Stripe checkout (skips the sign modal when already signed)
  const handleDirectCheckout = async () => {
    if (!portal?.id || directCheckoutLoading) return;
    logPortalActivity(portal.id, selectedProjectId, 'action', 'payment_initiated', undefined, activitySource);

    // Open window immediately to preserve user-gesture for popup blocker
    const checkoutWindow = window.open('', '_blank');
    if (checkoutWindow) {
      checkoutWindow.document.write(`
        <!DOCTYPE html><html><head><title>Redirecting to checkout...</title>
        <style>
          body { margin: 0; display: flex; align-items: center; justify-content: center; min-height: 100vh; background: #FAFAF8; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #2C3E2F; }
          .wrap { text-align: center; }
          .spinner { width: 36px; height: 36px; border: 3px solid #E5E7EB; border-top-color: #2C3E2F; border-radius: 50%; animation: spin .7s linear infinite; margin: 0 auto 16px; }
          @keyframes spin { to { transform: rotate(360deg); } }
          h2 { font-size: 18px; font-weight: 600; margin: 0 0 6px; }
          p { font-size: 14px; color: #6B7280; margin: 0; }
        </style></head><body><div class="wrap"><div class="spinner"></div><h2>Preparing checkout</h2><p>You'll be redirected to Stripe in a moment.</p></div></body></html>
      `);
      checkoutWindow.document.close();
    }
    setDirectCheckoutLoading(true);

    try {
      // Build services from local selection, fall back to agreement data saved at signing
      let services = designFeeServices.filter(s => selectedServices.includes(s.id));
      let total = services.reduce((sum, s) => sum + s.price, 0);

      if (!services.length && portal.agreementServices?.length) {
        services = portal.agreementServices.map(s => ({
          id: s.id, name: s.name, price: s.price, description: '',
        }));
        total = portal.agreementTotal || services.reduce((sum, s) => sum + s.price, 0);
      }

      // Last resort: if still no services, build from agreementTotal
      if (!services.length && portal.agreementTotal) {
        services = [{ id: 'design-fee', name: 'Design Services', price: portal.agreementTotal, description: '' }];
        total = portal.agreementTotal;
      }

      const functionsBase = process.env.REACT_APP_FUNCTIONS_URL
        || `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID || 'lctf-projects'}.cloudfunctions.net`;

      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        throw new Error('Sign in required to continue to checkout');
      }
      const resp = await fetch(`${functionsBase}/createDesignFeeCheckout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          portalId: portal.id,
          portalSlug: portal.slug || slug,
          engagementLetterId: effectivePortal?.engagementLetterId || null,
          services: services.map(s => ({ id: s.id, name: s.name, amount: s.price })),
          totalAmount: total,
          signature: null,
          projectId: selectedProjectId || null,
        }),
      });

      const result = await resp.json();
      if (!resp.ok || !result.url) {
        checkoutWindow?.close();
        throw new Error(result.error || 'Failed to create checkout session');
      }

      if (checkoutWindow) {
        checkoutWindow.location.href = result.url;
      } else {
        window.open(result.url, '_blank');
      }
      setAwaitingPayment(true);
      setDirectCheckoutLoading(false);

      let isFirstSnapshot = true;
      paymentUnsubRef.current = onSnapshot(doc(db, 'clientPortals', portal.id), (snap) => {
        if (isFirstSnapshot) { isFirstSnapshot = false; return; }
        const data = snap.data();
        const paid = snapshotShowsDesignFeePaid(data, selectedProjectId);
        if (paid) {
          const paidAmount = selectedProjectId
            ? (data?.projects?.[selectedProjectId]?.amountPaid || data?.amountPaid || total)
            : (data?.amountPaid || total);
          setAwaitingPayment(false);
          paymentUnsubRef.current?.();
          paymentUnsubRef.current = null;
          setPortal(prev => prev ? {
            ...prev,
            designFeePaid: true,
            designFeePaidAt: new Date(),
            amountPaid: paidAmount,
            currentStage: '3d-model',
          } : prev);
          if (selectedProjectId && portal.projects) {
            setPortal(prev => {
              if (!prev?.projects) return prev;
              const updated = { ...prev.projects };
              if (updated[selectedProjectId]) {
                updated[selectedProjectId] = {
                  ...updated[selectedProjectId],
                  designFeePaid: true,
                  designFeePaidAt: new Date(),
                  currentStage: '3d-model' as const,
                };
              }
              return { ...prev, projects: updated };
            });
          }
        }
      });
    } catch (err) {
      console.error('[DirectCheckout] Error:', err);
      setDirectCheckoutLoading(false);
    }
  };

  // Lightbox helpers
  const openLightbox = (images: Array<{ url: string; name: string }>, startIndex: number) => {
    setLightboxImages(images);
    setLightboxIndex(startIndex);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };
  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = '';
  };

  // Navigate to proposal page, pre-verifying PIN so client doesn't re-enter it
  const handleProposalNavigate = (proposalSlug: string, anchor?: string) => {
    if (portal?.id) logPortalActivity(portal.id, selectedProjectId, 'link_click', 'clicked_proposal', { proposalSlug }, activitySource);
    sessionStorage.setItem(`proposal_verified_${proposalSlug}`, 'true');
    const q = new URLSearchParams({
      from: 'portal',
      portalSlug: portal?.slug || '',
    });
    if (resolvedStakeholderToken) q.set('stakeholderToken', resolvedStakeholderToken);
    if (selectedProjectId) {
      q.set('project', selectedProjectId);
      const tabName = portal?.projects?.[selectedProjectId]?.projectName?.trim();
      if (tabName) q.set('displayProjectName', tabName);
    }
    navigate(`/proposal/${proposalSlug}?${q.toString()}${anchor ? `#${anchor}` : ''}`);
  };

  // Navigate to the public change-order page (handles its own auth + signing).
  const handleChangeOrderNavigate = (co: ChangeOrder) => {
    if (portal?.id) {
      logPortalActivity(
        portal.id,
        selectedProjectId,
        'link_click',
        'clicked_change_order',
        { changeOrderSlug: co.slug, reference: getChangeOrderReference(co) },
        activitySource,
      );
    }
    navigate(`/change-order/${co.slug}`);
  };

  const stakeholderQuerySuffix = resolvedStakeholderToken
    ? `&stakeholderToken=${encodeURIComponent(resolvedStakeholderToken)}`
    : '';

  // Get step link
  const getStepLink = (step: TimelineStep, portal: ClientPortal): string | null => {
    const status = effectivePortal ? resolveStepStatus(step, effectivePortal) : 'locked';
    if (status === 'locked' || status === 'skipped') return null;
    
    switch (step.id) {
      case 'design-fee':
        return portal.engagementLetterSlug 
          ? `/engagement/${portal.engagementLetterSlug}?from=portal&portalSlug=${portal.slug}${stakeholderQuerySuffix}` 
          : null;
      case 'quote':
      case 'quote-signed': {
        const slug = proposalSlugForDisplay;
        if (!slug || !isProposalVisibleToClient(proposalStatus)) return null;
        const params = new URLSearchParams({ from: 'portal', portalSlug: portal.slug });
        if (resolvedStakeholderToken) params.set('stakeholderToken', resolvedStakeholderToken);
        if (selectedProjectId) {
          params.set('project', selectedProjectId);
          const tabName = portal.projects?.[selectedProjectId]?.projectName?.trim();
          if (tabName) params.set('displayProjectName', tabName);
        }
        return `/proposal/${slug}?${params.toString()}`;
      }
      case '3d-model':
        return portal.modelUrl || portal.modelImageUrl || null;
      case '2d-plans':
        return portal.plansUrl || null;
      default:
        return null;
    }
  };
  
  // Loading state (include auth so staff never briefly see PIN before Firebase restores session)
  if (authLoading || loading) {
    return (
      <div className="cps-page">
        <div className="cps-loading">
          <Loader2 className="cps-spinner" size={40} />
          <p>Loading your project portal...</p>
        </div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="cps-page">
        <div className="cps-error">
          <div className="cps-error-icon">
            <X size={32} />
          </div>
          <h2>Unable to Load Portal</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }
  
  // PIN entry — uses the same background fallback chain as the post-unlock portal
  // (per-portal > global > engagement) so the gate matches the rest of the experience.
  if (requiresPin) {
    const pinBgUrl =
      globalPortalBgImage?.url || engagementBackground?.url || null;
    return (
      <div className="cps-page cps-pin-page">
        {pinBgUrl && (
          <div
            className="cps-bg"
            style={{ backgroundImage: `url(${pinBgUrl})` }}
          />
        )}
        <div className="cps-bg-overlay" style={{ background: portalOverlayColor, opacity: portalOverlayOpacity }} />

        <div className="cps-pin-card">
          <header className="cps-pin-brand">
            <img
              src="/assets/logo/lctf-logo-portal-pin-transparent.png"
              alt="Leiper's Creek Timber Frames"
              className="cps-pin-logo-img"
            />
          </header>

          <h1>Enter Your PIN</h1>
          <p>Please enter your 4-digit PIN to access your project portal</p>
          
          <div className="cps-pin-inputs" onPaste={handlePinPaste}>
            {pinDigits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (pinInputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handlePinChange(index, e.target.value)}
                onKeyDown={(e) => handlePinKeyDown(index, e)}
                className={`cps-pin-input ${digit ? 'filled' : ''}`}
                autoFocus={index === 0}
              />
            ))}
          </div>
          
          {pinError && <p className="cps-pin-error">{pinError}</p>}
          
          <button
            className="cps-pin-btn"
            onClick={verifyPin}
            disabled={verifyingPin || pinDigits.some(d => !d)}
          >
            {verifyingPin ? (
              <>
                <Loader2 className="cps-btn-spinner" size={18} />
                Verifying...
              </>
            ) : (
              'Access Portal'
            )}
          </button>
        </div>
      </div>
    );
  }
  
  if (!portal) return null;
  
  const stageOrder: PortalStage[] = [
    'design-fee', '3d-model', 'quote', 'quote-signed', '2d-plans', 'in-production', 'delivery'
  ];
  
  // Resolve background: per-portal override > global portal bg > engagement background
  const resolvedBgImage = portal?.portalBackgroundImage?.url || globalPortalBgImage?.url || engagementBackground?.url;
  const resolvedOverlayColor = portal?.portalOverlayColor || portalOverlayColor;
  const resolvedOverlayOpacity = portal?.portalOverlayOpacity ?? portalOverlayOpacity;

  return (
    <div className="cps-page">
      {/* Background */}
      {resolvedBgImage && (
        <div
          className="cps-bg"
          style={{ backgroundImage: `url(${resolvedBgImage})` }}
        />
      )}
      <div className="cps-bg-overlay" style={{ background: resolvedOverlayColor, opacity: resolvedOverlayOpacity }} />
      
      {/* Main Container */}
      <div className="cps-container">
        {/* Payment Result Banner */}
        {paymentStatus === 'success' && (
          <div className="cps-payment-thankyou">
            <button className="cps-payment-thankyou-dismiss" onClick={() => setPaymentStatus(null)} aria-label="Dismiss"><X size={16} /></button>
            <div className="cps-payment-thankyou-icon">
              <CheckCircle size={32} />
            </div>
            <h2 className="cps-payment-thankyou-title">Thank You for Your Payment!</h2>
            <p className="cps-payment-thankyou-body">
              Your payment has been received and our team has been notified.
              We'll begin working on your project right away.
            </p>
          </div>
        )}
        {paymentStatus === 'cancelled' && (
          <div className="cps-payment-banner cps-payment-cancelled">
            <X size={18} />
            <span>Payment was cancelled. You can try again below.</span>
            <button onClick={() => setPaymentStatus(null)} aria-label="Dismiss"><X size={14} /></button>
          </div>
        )}

        {isSampleGcPortal ? null : isStakeholderViewer && stakeholderDisplayName && portal ? (
          <div
            className="cps-stakeholder-banner"
            role="status"
          >
            <strong>Welcome, {stakeholderDisplayName}</strong>
            {' — '}You&apos;re viewing as a <strong>stakeholder</strong> (view only). This portal is for{' '}
            <strong>{portal.clientName}</strong>. You can browse progress and open documents, but you cannot sign agreements or make payments.
          </div>
        ) : null}

        {/* Header */}
        <header className="cps-header">
          {brandingLogoSrc ? (
            <div className="cps-branded-logo-backdrop cps-branded-logo-backdrop--header">
              <img src={brandingLogoSrc} alt="Company Logo" className="cps-logo" onError={onBrandingLogoError} />
            </div>
          ) : (
            <h1 className="cps-company">Leiper's Creek Timber Frames</h1>
          )}
        </header>
        
        {/* Welcome Card */}
        <div className="cps-welcome-card">
          <div className="cps-welcome-content">
            <p className={`cps-welcome-label${isSampleGcPortal ? ' cps-welcome-label--demo' : ''}`}>
              {isSampleGcPortal ? 'Demo portal' : 'Project Portal'}
            </p>
            {isEditingName ? (
              <div className="cps-name-edit">
                <input
                  type="text"
                  value={editFirstName}
                  onChange={(e) => setEditFirstName(e.target.value)}
                  placeholder="First name"
                  className="cps-name-input"
                />
                <input
                  type="text"
                  value={editLastName}
                  onChange={(e) => setEditLastName(e.target.value)}
                  placeholder="Last name"
                  className="cps-name-input"
                />
                <div className="cps-name-edit-actions">
                  <button
                    type="button"
                    className="cps-name-save-btn"
                    onClick={handleSaveName}
                    disabled={savingName || !(editFirstName.trim() || editLastName.trim())}
                  >
                    {savingName ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
                    {savingName ? ' Saving...' : ' Save'}
                  </button>
                  <button
                    type="button"
                    className="cps-name-cancel-btn"
                    onClick={handleCancelEditName}
                    disabled={savingName}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : isSampleGcPortal ? (
              <h1 className="cps-welcome-name">
                {stakeholderDisplayName?.trim().split(/\s+/)[0]
                  ? `Welcome, ${stakeholderDisplayName.trim().split(/\s+/)[0]}`
                  : 'Welcome'}
              </h1>
            ) : isStakeholderViewer ? (
              <h1 className="cps-welcome-name">
                {portal.clientName.split(' ')[0]}&apos;s project portal
              </h1>
            ) : (
              <h1 className="cps-welcome-name">
                Welcome back, {portal.clientName.split(' ')[0]}
                <button
                  type="button"
                  className="cps-name-edit-btn"
                  onClick={handleStartEditName}
                  title="Edit name"
                  aria-label="Edit name"
                >
                  <Pencil size={14} />
                </button>
              </h1>
            )}
            
            {(() => {
              // Sample invites: only the invitee's company — never "Sample GC Partners".
              const welcomeCompany = isSampleGcPortal
                ? stakeholderCompanyName
                : portal.clientCompany;
              if (!welcomeCompany) return null;
              return (
                <div className="cps-company-row">
                  <span className="cps-company-display">
                    <Building2 size={14} />
                    <span>{welcomeCompany}</span>
                  </span>
                </div>
              );
            })()}

            <p className="cps-welcome-desc">
              {isSampleGcPortal
                ? 'Look around like this was your project. Nothing here is live.'
                : isStakeholderViewer
                ? `Track progress for ${portal.clientName}'s timber frame project below.`
                : 'Track the progress of your timber frame project below'}
            </p>
          </div>
          
          {/* Progress Ring - only when a project is selected */}
          {selectedProjectId && (
            <div className="cps-progress-ring">
              <svg viewBox="0 0 100 100">
                <circle className="cps-ring-bg" cx="50" cy="50" r="42" />
                <circle 
                  className="cps-ring-fill" 
                  cx="50" 
                  cy="50" 
                  r="42" 
                  strokeDasharray={`${progressPercent * 2.64} 264`}
                />
              </svg>
              <div className="cps-ring-text">
                <span className="cps-ring-percent">{progressPercent}%</span>
              </div>
            </div>
          )}
        </div>

        {/* Brand Video */}
        {brandVideoUrl && (
          <div className="cps-brand-video-card">
            <div className="cps-brand-video-wrapper">
              <iframe
                src={brandVideoUrl.includes('/embed/') ? brandVideoUrl : brandVideoUrl.replace('watch?v=', 'embed/')}
                title="Learn About Our Process"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        )}
        
        {/* No Active Projects — empty state */}
        {projectsList.length === 0 && portal && (
          <div className="cps-no-projects">
            {brandingLogoSrc ? (
              <div className="cps-branded-logo-backdrop cps-branded-logo-backdrop--empty-state">
                <img src={brandingLogoSrc} alt="Logo" className="cps-no-projects-logo" onError={onBrandingLogoError} />
              </div>
            ) : (
              <span className="cps-no-projects-brand">Leiper's Creek Timber Frames</span>
            )}
            <h2 className="cps-no-projects-heading">No Active Projects</h2>
            <p className="cps-no-projects-sub">
              There are no active projects on your portal at this time.<br />
              If you believe this is an error, please reach out to your project manager.
            </p>
          </div>
        )}

        {/* Project Selection Cards */}
        {projectsList.length > 1 && !selectedProjectId && (
          <div className="cps-project-cards">
            <h2 className="cps-projects-title">Your Projects</h2>
            <div className="cps-projects-grid">
              {projectsList.map(proj => {
                const stageLabel = TIMELINE_STEPS.find(s => s.id === proj.currentStage)?.title || proj.currentStage;
                const parsed = getPortalProjectDisplay(proj, portal?.clientCompany);
                const cardBond = proj.bond ? portalBondCopy(proj.bond) : null;
                return (
                  <button
                    key={proj.dealId}
                    className="cps-project-card"
                    onClick={() => setSelectedProjectId(proj.dealId)}
                  >
                    <div className="cps-project-card-head">
                      {parsed.projectNumber && (
                        <span className="cps-num-pill">{parsed.projectNumber}</span>
                      )}
                      <span className="cps-project-card-stage">{stageLabel}</span>
                      {cardBond && (
                        <span className={`cps-bond-pill is-${cardBond.tone}`} title={cardBond.detail}>
                          <ShieldCheck size={11} aria-hidden /> {cardBond.pill}
                        </span>
                      )}
                    </div>
                    <h3 className="cps-project-card-name">{parsed.descriptor}</h3>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Project context bar — multi-project: tight title row + meta */}
        {projectsList.length > 1 && selectedProjectId && effectivePortal && multiProjectContext && (
          <div className="cps-project-context-bar" role="region" aria-label="Current project">
            <div className="cps-project-context-col">
              <div className="cps-project-context-title-row">
                {multiProjectContext.parsed.projectNumber && (
                  <span className="cps-num-pill cps-num-pill--lg">
                    {multiProjectContext.parsed.projectNumber}
                  </span>
                )}
                <h2 className="cps-project-context-name">
                  {multiProjectContext.parsed.descriptor}
                </h2>
              </div>
              <div className="cps-project-context-meta" aria-live="polite">
                <span className="cps-project-context-stage">
                  {TIMELINE_STEPS.find(
                    s =>
                      s.id ===
                      (portal?.projects?.[selectedProjectId]?.currentStage || effectivePortal.currentStage)
                  )?.title || effectivePortal.currentStage}
                </span>
                <span className="cps-project-context-meta-sep" aria-hidden>·</span>
                <span className="cps-project-context-position">
                  Project {multiProjectContext.indexOneBased} of {multiProjectContext.total}
                </span>
                {portalBond && (
                  <>
                    <span className="cps-project-context-meta-sep" aria-hidden>·</span>
                    <span className={`cps-bond-pill is-${portalBond.copy.tone}`} title={portalBond.copy.detail}>
                      <ShieldCheck size={11} aria-hidden /> {portalBond.copy.pill}
                    </span>
                  </>
                )}
              </div>
            </div>
            <button
              type="button"
              className="cps-view-all-projects-btn"
              onClick={() => {
                setSelectedProjectId(null);
                setProposalStatus(null);
                setResolvedProposalSlug(null);
              }}
            >
              <LayoutGrid size={16} aria-hidden />
              View all projects
            </button>
          </div>
        )}


        {/* Design approval trail — additive; renders nothing for portals with no releases. */}
        {projectsList.length > 0 &&
          (projectsList.length <= 1 || selectedProjectId) &&
          effectivePortal &&
          portalReleases.length > 0 && (
            <div className="client-portal-theme" id="cps-plan-approvals">
              <PortalApprovalTrailBoundary>
                <PortalApprovalTrail
                  {...(portalTrailInput || {
                    releases: portalReleases,
                    bundles: portalBundles,
                    structures: [],
                    structureGroups: [],
                    legacyApprovals: groupPortalDealApprovals(effectivePortal),
                    proposalApprovedAt,
                    proposalApprovedByName,
                  })}
                  readOnly={isSampleGcPortal || isStakeholderViewer}
                  onOpenItem={item => {
                    if (portal?.id) {
                      logPortalActivity(
                        portal.id,
                        selectedProjectId,
                        'link_click',
                        'viewed_released_version',
                        {
                          streamKind: item.release.streamKind,
                          portalTarget: item.release.portalTarget,
                          releaseId: item.release.id,
                          name: item.release.name,
                          releaseVersionIndex: item.release.releaseVersionIndex,
                          source: 'approval_trail',
                        },
                        activitySource,
                      );
                    }
                    const approval =
                      item.status === 'approved' ? item.approval || approvalForOpenedRelease(item.release) : null;
                    const queue = item.releases.length > 1 ? item.releases : undefined;
                    openReleaseFile(
                      item.release,
                      approval,
                      queue,
                      formatSharedModelApprovalNote(item.sharedModelGroup) || undefined,
                    );
                    if (item.status === 'awaiting' && !isSampleGcPortal && !isStakeholderViewer) {
                      setViewerAwaitingItem({
                        deliverableType: item.deliverableType,
                        releases: item.releases.length > 0 ? item.releases : [item.release],
                        bundleId: item.bundle?.id,
                        releaseVersionIndex: item.releaseVersionIndex,
                        releaseId: item.release.id,
                        label: `${item.displayLabel} v${item.releaseVersionIndex}`,
                        sharedModelNote: formatSharedModelApprovalNote(item.sharedModelGroup) || undefined,
                      });
                    }
                  }}
                  onApprove={item => {
                    if (item.deliverableType === 'plans') {
                      openDrawingsWalkthrough(
                        item.releases.length > 0 ? item.releases : [item.release],
                        item.bundle?.id,
                        item.releaseVersionIndex,
                      );
                      return;
                    }
                    setApprovalModal({
                      type: item.deliverableType,
                      releaseId: item.release.id,
                      bundleId: item.bundle?.id,
                      releaseVersionIndex: item.releaseVersionIndex,
                      releaseIds: item.releases.map(r => r.id || '').filter(Boolean),
                      label: `${item.displayLabel} v${item.releaseVersionIndex}`,
                      sharedModelNote: formatSharedModelApprovalNote(item.sharedModelGroup) || undefined,
                    });
                  }}
                />
              </PortalApprovalTrailBoundary>
            </div>
          )}

        {/* Timeline — show when single project or project selected (but not when zero projects) */}
        {projectsList.length > 0 && (projectsList.length <= 1 || selectedProjectId) && effectivePortal && (
        <div className="cps-timeline">

          {TIMELINE_STEPS.map((step, index) => {
            const ep = timelinePortal;
            if (!ep) return null;
            const status = effectivePortal ? resolveStepStatus(step, effectivePortal) : 'locked';
            const portalForLink = proposalSlugForDisplay ? { ...ep, proposalSlug: proposalSlugForDisplay } : ep;
            const link = getStepLink(step, portalForLink);
            const isExternal = link?.startsWith('http');
            const isDesignFeeStep = step.id === 'design-fee';
            const showActions = (status === 'active' || status === 'completed') && isDesignFeeStep;
            const plansPointUp =
              step.id === '2d-plans' &&
              (status === 'active' || status === 'completed') &&
              portalReleases.length > 0;
            const conceptVersionHistory =
              step.id === '3d-model' &&
              (status === 'active' || status === 'completed') &&
              (conceptPhaseModelBundles.length > 0 || conceptBundles.length > 0) ? (
                <PortalModelStepVersionsPopover
                  variant="header"
                  modelBundles={conceptPhaseModelBundles}
                  modelReleases={conceptPhaseModels}
                  conceptBundles={conceptBundles}
                  conceptReleases={releasedConceptElevations}
                  modelApproval={effectivePortal?.modelApproval}
                  modelApprovalHistory={effectivePortal?.modelApprovalHistory}
                  conceptApproval={effectivePortal?.conceptApproval}
                  conceptApprovalHistory={effectivePortal?.conceptApprovalHistory}
                  onOpenRelease={r => {
                    if (portal?.id) {
                      logPortalActivity(
                        portal.id,
                        selectedProjectId,
                        'link_click',
                        'viewed_released_version',
                        {
                          streamKind: r.streamKind,
                          portalTarget: r.portalTarget,
                          releaseId: r.id,
                          name: r.name,
                          releaseVersionIndex: r.releaseVersionIndex,
                        },
                        activitySource,
                      );
                    }
                    openReleaseFile(r, approvalForOpenedRelease(r));
                  }}
                  onOpenWalkthrough={r => {
                    if (!portal?.id) return;
                    logPortalActivity(
                      portal.id,
                      selectedProjectId,
                      'link_click',
                      'viewed_3d_loom',
                      {
                        releaseId: r.id,
                        releaseVersionIndex: r.releaseVersionIndex,
                        context: 'version_history',
                      },
                      activitySource,
                    );
                  }}
                />
              ) : null;
            
            return (
              <div 
                key={step.id}
                id={`cps-step-${step.id}`}
                className={`cps-step ${status}`}
              >
                {/* Step Card */}
                <div className="cps-step-card">
                  <div className="cps-step-header">
                    <div className={`cps-step-icon ${status}`}>
                      {status === 'locked' ? <Lock size={20} /> : step.icon}
                    </div>
                    <div className="cps-step-info">
                      <div className="cps-step-title-row">
                        <h3>{step.title}</h3>
                        {(status === 'completed' || status === 'active' || (isDesignFeeStep && ep.designFeePaid)) && (
                          <div className="cps-badge-row">
                            {!conceptSectionCollapsible && conceptVersionHistory}
                            {isDesignFeeStep && ep.designFeePaid && (
                              <span className="cps-paid-badge">
                                <CheckCircle size={12} />
                                Paid
                              </span>
                            )}
                            {status === 'active' && (
                              <span className="cps-active-badge">Current</span>
                            )}
                            {step.id === 'quote' && status === 'active' && proposalIsRom && (
                              <span className="cps-rom-badge">ROM</span>
                            )}
                            {step.id === 'quote' && status === 'active' && (!proposalStatus || proposalStatus === 'draft') && (
                              <span className="cps-in-progress-badge">Proposal in Progress</span>
                            )}
                          </div>
                        )}
                      </div>
                      <p>
                        {status === 'skipped'
                          ? 'Skipped'
                          : plansPointUp
                            ? 'Review and approve your plans above.'
                            : step.description}
                      </p>
                    </div>
                    {step.id === '3d-model' && conceptSectionCollapsible && (status === 'active' || status === 'completed') && (
                      <div className="cps-step-header-tools">
                        {conceptVersionHistory}
                        <button
                          type="button"
                          className={`cps-step-collapse${conceptDeliverablesOpen ? ' is-open' : ''}`}
                          aria-expanded={conceptDeliverablesOpen}
                          aria-label={conceptDeliverablesOpen ? 'Hide concept deliverables' : 'Show concept deliverables'}
                          onClick={() => setConceptDeliverablesToggled(!conceptDeliverablesOpen)}
                        >
                          <ChevronDown size={18} aria-hidden />
                        </button>
                      </div>
                    )}
                    {step.id === '2d-plans' && !plansPointUp && engineeredTableReady && (status === 'active' || status === 'completed') && releasedPlans.length > 0 && (
                      <div className="cps-step-header-tools">
                        <PortalReleasedVersionsPopover
                          bundles={planBundles}
                          releases={releasedPlans}
                          heading="Engineered Drawings"
                          onOpenRelease={r => {
                            if (portal?.id) {
                              logPortalActivity(
                                portal.id,
                                selectedProjectId,
                                'link_click',
                                'viewed_released_version',
                                { streamKind: 'two_d_pdf', releaseId: r.id, name: r.name, releaseVersionIndex: r.releaseVersionIndex },
                                activitySource,
                              );
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {isDesignFeeStep &&
                    effectivePortal?.inspirationBoardPortalEnabled &&
                    selectedProjectId &&
                    (status === 'active' || status === 'completed' || status === 'skipped') && (
                    <div
                      id={`cps-inspiration-board-${selectedProjectId}`}
                      className="cps-board-cta"
                    >
                      <div className="cps-board-cta-icon">
                        <LayoutGrid size={20} aria-hidden />
                      </div>
                      <div className="cps-board-cta-info">
                        <span className="cps-board-cta-title">Inspiration Board</span>
                        <span className="cps-board-cta-hint">
                          {isStakeholderViewer
                            ? 'Browse images, links, and notes for this project.'
                            : 'Add images, links, and notes to capture your vision.'}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="cps-action-btn cps-btn-primary cps-board-cta-btn"
                        onClick={() => {
                          if (portal?.id) {
                            logPortalActivity(
                              portal.id,
                              selectedProjectId,
                              'link_click',
                              'opened_inspiration_board',
                              {
                                page: 'portal_inspiration_board',
                                context: 'design_fee_cta',
                                projectName: projectsList.find(p => p.dealId === selectedProjectId)?.projectName,
                              },
                              activitySource,
                            );
                          }
                          navigate(`/portal/${slug}/board?project=${selectedProjectId}`);
                        }}
                      >
                        Open Board
                        <ArrowRight size={16} aria-hidden />
                      </button>
                    </div>
                  )}

                  {/* Design Fee Step — Get Started Flow */}
                  {isDesignFeeStep && (status === 'active' || status === 'completed') && (() => {
                    const isPaid = ep.designFeePaid;
                    const total = designFeeServices
                      .filter(s => selectedServices.includes(s.id))
                      .reduce((sum, s) => sum + s.price, 0);

                    if (isPaid) {
                      const showReceipt = !!(ep.stripeReceiptUrl || ep.stripeSessionId);
                      return (
                        <div style={{ marginTop: 12, padding: '12px 16px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          <CheckCircle size={18} style={{ color: '#16A34A', flexShrink: 0 }} />
                          <span style={{ fontSize: 14, fontWeight: 600, color: '#166534' }}>
                            Payment received{ep.amountPaid ? ` — $${ep.amountPaid.toLocaleString()}` : ''}
                          </span>
                          {(showReceipt || engagementSigned) && (
                            <div style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                              {showReceipt && (
                                <button
                                  type="button"
                                  onClick={() => void handleDownloadStripeReceipt()}
                                  disabled={loadingReceipt}
                                  style={{
                                    fontSize: 12, color: '#2C3E2F', background: 'none',
                                    border: 'none', cursor: loadingReceipt ? 'wait' : 'pointer',
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    textDecoration: 'underline', padding: 0, fontFamily: 'inherit',
                                  }}
                                >
                                  {loadingReceipt ? <Loader2 size={12} className="cps-spinner" /> : <Receipt size={12} />}
                                  {loadingReceipt ? 'Loading...' : 'Download Receipt'}
                                </button>
                              )}
                              {engagementSigned && (
                                <button
                                  type="button"
                                  onClick={handleViewSignedAgreement}
                                  disabled={generatingPdf}
                                  style={{
                                    fontSize: 12, color: '#2C3E2F', background: 'none',
                                    border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
                                    textDecoration: 'underline', padding: 0, fontFamily: 'inherit',
                                  }}
                                >
                                  {generatingPdf ? <Loader2 size={12} className="cps-spinner" /> : <FileSignature size={12} />}
                                  {generatingPdf ? 'Generating...' : 'Signed Agreement'}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }

                    if (!showServiceSelection) {
                      return (
                        <div className="cps-step-actions" style={{ marginTop: 12 }}>
                          <button
                            onClick={() => {
                              setShowServiceSelection(true);
                              if (portal?.id) logPortalActivity(portal.id, selectedProjectId, 'action', 'design_fee_get_started', undefined, activitySource);
                            }}
                            className="cps-action-btn cps-btn-primary"
                            style={{ border: 'none', cursor: 'pointer' }}
                          >
                            Get Started
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      );
                    }

                    const isLocked = engagementSigned;

                    return (
                      <div style={{ marginTop: 12, padding: '16px', background: '#FAFAF8', border: '1px solid #E8E5E0', borderRadius: 10 }}>
                        <h4 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: '#1a1a1a' }}>Select Your Design Services</h4>
                        <p style={{ margin: '0 0 12px', fontSize: 12, color: '#6B7280' }}>
                          {isLocked
                            ? 'Your selections have been locked in with your signed agreement.'
                            : 'Choose the deliverables you\'d like included in your concept design package.'}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16, opacity: isLocked ? 0.6 : 1, pointerEvents: isLocked ? 'none' : 'auto' }}>
                          {designFeeServices.map(svc => {
                            const isSelected = selectedServices.includes(svc.id);
                            return (
                              <label
                                key={svc.id}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                                  background: isSelected ? '#fff' : '#F9FAFB',
                                  border: isSelected ? '2px solid #2C3E2F' : '2px solid #E5E7EB',
                                  borderRadius: 8, cursor: isLocked ? 'default' : svc.required ? 'default' : 'pointer',
                                  transition: 'border-color 0.15s',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  disabled={isLocked || svc.required || isStakeholderViewer}
                                  onChange={() => {
                                    if (isLocked || svc.required || isStakeholderViewer) return;
                                    const wasSelected = selectedServices.includes(svc.id);
                                    if (portal?.id) {
                                      logPortalActivity(portal.id, selectedProjectId, 'action',
                                        wasSelected ? 'service_deselected' : 'service_selected',
                                        { serviceId: svc.id, serviceName: svc.name, price: svc.price },
                                        activitySource,
                                      );
                                    }
                                    setSelectedServices(prev => {
                                      const updated = prev.includes(svc.id) ? prev.filter(id => id !== svc.id) : [...prev, svc.id];
                                      if (portal?.id) {
                                        // Nested projects.* and agreement fields are server-gated;
                                        // persist via Admin SDK callable (rules allowlist is dealId-only).
                                        savePortalSelectedServices({
                                          portalId: portal.id,
                                          selectedServices: updated,
                                          projectId: selectedProjectId || null,
                                        }).catch((err) => {
                                          console.error('[Portal] Failed to persist selectedServices:', err);
                                        });
                                      }
                                      return updated;
                                    });
                                  }}
                                  style={{ width: 18, height: 18, accentColor: '#2C3E2F' }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>
                                    {svc.name}
                                    {svc.required && <span style={{ fontSize: 10, fontWeight: 500, color: '#6B7280', marginLeft: 6 }}>Required</span>}
                                    {!svc.required && <span style={{ fontSize: 10, fontWeight: 500, color: '#C9A227', marginLeft: 6 }}>Add-on</span>}
                                  </div>
                                  <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.4 }}>{svc.description}</div>
                                  {svc.id === '3d-model' && sample3DModelUrl && (
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault(); e.stopPropagation(); setShowSample3D(true);
                                        if (portal?.id) logPortalActivity(portal.id, selectedProjectId, 'link_click', 'viewed_sample_3d_model', undefined, activitySource);
                                      }}
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6,
                                        padding: '4px 10px', fontSize: 11, fontWeight: 600, color: '#2C3E2F',
                                        background: 'rgba(44,62,47,0.08)', border: '1px solid rgba(44,62,47,0.15)',
                                        borderRadius: 6, cursor: 'pointer', transition: 'all 0.15s',
                                      }}
                                      onMouseEnter={e => { (e.target as HTMLElement).style.background = 'rgba(44,62,47,0.14)'; }}
                                      onMouseLeave={e => { (e.target as HTMLElement).style.background = 'rgba(44,62,47,0.08)'; }}
                                    >
                                      <Eye size={12} /> View Sample 3D Model
                                    </button>
                                  )}
                                </div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: '#2C3E2F', flexShrink: 0 }}>
                                  ${svc.price.toLocaleString()}
                                </div>
                              </label>
                            );
                          })}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#2C3E2F', borderRadius: 8, marginBottom: 12 }}>
                          <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>Total</span>
                          <span style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>${total.toLocaleString()}</span>
                        </div>

                        {awaitingPayment && engagementSigned && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, marginBottom: 12, fontSize: 13, color: '#9A3412' }}>
                            <Loader2 size={14} className="cps-spinner" />
                            <span style={{ flex: 1 }}>Waiting for payment confirmation...</span>
                            <button
                              onClick={() => setAwaitingPayment(false)}
                              style={{ background: 'none', border: 'none', color: '#9A3412', cursor: 'pointer', padding: 2, fontSize: 16, lineHeight: 1 }}
                              aria-label="Dismiss"
                            >×</button>
                          </div>
                        )}

                        {isStakeholderViewer && engagementSigned ? (
                          <button
                            onClick={handleViewSignedAgreement}
                            disabled={generatingPdf}
                            className="cps-action-btn cps-btn-secondary"
                            style={{ justifyContent: 'center', width: '100%', border: 'none', cursor: 'pointer' }}
                          >
                            {generatingPdf ? <Loader2 size={16} className="cps-spinner" /> : <FileSignature size={16} />}
                            {generatingPdf ? 'Generating...' : 'View signed agreement'}
                          </button>
                        ) : isStakeholderViewer ? (
                          <p style={{ margin: 0, padding: '12px 14px', fontSize: 13, color: '#6B7280', background: '#FAFAF8', borderRadius: 8, border: '1px solid #E8E5E0' }}>
                            View only — payment and agreements are completed by the client.
                          </p>
                        ) : !engagementSigned ? (
                          <button
                            onClick={() => {
                              setShowSignModal(true);
                              logPortalActivity(portal!.id!, selectedProjectId, 'action', 'opened_sign_modal', undefined, activitySource);
                            }}
                            className="cps-action-btn cps-btn-primary"
                            style={{ justifyContent: 'center', width: '100%', border: 'none', cursor: 'pointer' }}
                          >
                            <CreditCard size={16} /> Make Payment <ChevronRight size={16} />
                          </button>
                        ) : (
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button
                              onClick={handleViewSignedAgreement}
                              disabled={generatingPdf}
                              className="cps-action-btn cps-btn-secondary"
                              style={{ flex: 1, justifyContent: 'center', border: 'none', cursor: 'pointer' }}
                            >
                              {generatingPdf ? <Loader2 size={16} className="cps-spinner" /> : <FileSignature size={16} />}
                              {generatingPdf ? 'Generating...' : 'Signed Agreement'}
                            </button>
                            <button
                              onClick={handleDirectCheckout}
                              disabled={directCheckoutLoading}
                              className="cps-action-btn cps-btn-primary"
                              style={{ justifyContent: 'center', flex: 1, border: 'none', cursor: 'pointer' }}
                            >
                              {directCheckoutLoading ? (
                                <><Loader2 size={16} className="cps-spinner" /> Processing...</>
                              ) : (
                                <><CreditCard size={16} /> Make Payment <ChevronRight size={16} /></>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Project Timeline — shown on design-fee step when it is the active stage */}
                  {step.id === 'design-fee' && status === 'active' && (
                    <ProjectTimeline
                      phases={designFeeTimelinePhases}
                      milestones={designFeeMilestones}
                      savedTargetDate={savedTargetDate}
                      onTargetDateChange={handleTargetDateChange}
                    />
                  )}

                  {/* View Link for Other Steps (hide "Your Quote" button once proposal is signed) */}
                  {link && !isDesignFeeStep && step.id !== '3d-model' && !plansPointUp && !(step.id === '2d-plans' && engineeredTableReady) && (status === 'completed' || status === 'active') && !(step.id === 'quote' && proposalActuallySigned) && (
                    <div className="cps-step-actions">
                      {isExternal ? (
                        <a 
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cps-action-btn cps-btn-secondary"
                          onClick={() => { if (portal?.id) logPortalActivity(portal.id, selectedProjectId, 'link_click', `viewed_${step.id}`, undefined, activitySource); }}
                        >
                          View
                          <ExternalLink size={14} />
                        </a>
                      ) : step.id === 'quote' && proposalSlugForDisplay ? (
                        <button
                          className={`cps-action-btn ${
                            canOfferSignProposal ? 'cps-btn-primary' : 'cps-btn-secondary'
                          }`}
                          onClick={() =>
                            handleProposalNavigate(
                              proposalSlugForDisplay,
                              canOfferSignProposal ? 'proposal-signature' : undefined,
                            )
                          }
                        >
                          {portalProposalCtaLabel({
                            proposalSigned: proposalActuallySigned,
                            canOfferSign: canOfferSignProposal,
                          })}
                          <ChevronRight size={16} />
                        </button>
                      ) : step.id === 'quote-signed' && proposalSlugForDisplay && proposalActuallySigned ? (
                        <button
                          className="cps-action-btn cps-btn-secondary"
                          onClick={() => handleProposalNavigate(proposalSlugForDisplay)}
                        >
                          View Signed Proposal
                          <ChevronRight size={16} />
                        </button>
                      ) : (
                        <Link to={link} className="cps-action-btn cps-btn-secondary">
                          View
                          <ChevronRight size={16} />
                        </Link>
                      )}
                      {step.id === '2d-plans' && primaryPlanRelease && isDesktopPortal && (
                        <button
                          type="button"
                          className="cps-action-btn cps-btn-secondary"
                          data-tour="portal-pdf-markup-btn"
                          onClick={() => {
                            setPdfMarkupTourOnOpen(
                              !hasSeenPortalPdfMarkupTour() && !isStakeholderViewer,
                            );
                            openApprovedPdf(primaryPlanRelease);
                            if (portal?.id) {
                              logPortalActivity(
                                portal.id,
                                selectedProjectId,
                                'link_click',
                                'opened_portal_pdf_markup',
                                { releaseId: primaryPlanRelease.id },
                                activitySource,
                              );
                            }
                          }}
                        >
                          Review &amp; mark up
                          <ChevronRight size={16} />
                        </button>
                      )}
                      {step.id === '2d-plans' && primaryPlanRelease && !isSampleGcPortal && !isStakeholderViewer && (
                        isDeliverableApproved(
                          effectivePortal?.plansApproval,
                          primaryPlanRelease.id,
                        ) ? (
                          <span className="cps-approval-badge">
                            <CheckCircle size={14} aria-hidden />
                            {formatApprovalBadge(effectivePortal?.plansApproval)}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="cps-action-btn cps-btn-primary"
                            onClick={() =>
                              openDrawingsWalkthrough(
                                planPackageReleases.length > 0
                                  ? planPackageReleases
                                  : [primaryPlanRelease],
                                currentPlanBundle?.id,
                                primaryPlanRelease.releaseVersionIndex,
                              )
                            }
                          >
                            <FileSignature size={14} />
                            Approve {drawingApprovalLabel(primaryPlanRelease)}
                          </button>
                        )
                      )}
                      {step.id === '2d-plans' && (
                        <PortalReleasedVersionsPopover
                          bundles={planBundles}
                          releases={releasedPlans}
                          heading="Engineered Drawings"
                          onOpenRelease={r => {
                            if (portal?.id) {
                              logPortalActivity(
                                portal.id,
                                selectedProjectId,
                                'link_click',
                                'viewed_released_version',
                                { streamKind: 'two_d_pdf', releaseId: r.id, name: r.name, releaseVersionIndex: r.releaseVersionIndex },
                                activitySource,
                              );
                            }
                          }}
                        />
                      )}
                    </div>
                  )}

                  {plansPointUp && (
                    <div className="cps-step-actions">
                      <button
                        type="button"
                        className="cps-action-btn cps-btn-primary"
                        style={{ border: 'none' }}
                        onClick={() => {
                          document.getElementById('cps-plan-approvals')?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                          });
                        }}
                      >
                        Plan approvals
                        <ArrowUp size={16} aria-hidden />
                      </button>
                    </div>
                  )}

                  {step.id === '2d-plans' && !plansPointUp && engineeredTableReady && (status === 'active' || status === 'completed') && (
                    <div className="cps-deliverables-table-wrap" style={{ marginTop: 12 }}>
                      <table className="cps-deliverables-table cps-deliverables-table--with-latest">
                        <thead>
                          <tr>
                            <th scope="col">Latest</th>
                            <th scope="col">Deliverable</th>
                            <th scope="col">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {planPackageReleases.map((release, idx) => (
                            <tr key={release.id}>
                              <td>
                                {idx === 0 && (
                                  <DeliverableLatestVersionCell
                                    releaseVersionIndex={release.releaseVersionIndex}
                                    releasedAt={currentPlanBundle?.releasedAt ?? release.releasedAt}
                                  />
                                )}
                              </td>
                              <td>
                                <span className="cps-deliverable-name">
                                  <Layers2 size={15} aria-hidden />
                                  {planPackageReleases.length === 1
                                    ? '2D Drawings'
                                    : release.displayLabel || release.name}
                                </span>
                              </td>
                              <td>
                                <div className="cps-deliverable-actions">
                                  <button
                                    type="button"
                                    className="cps-deliverable-action"
                                    onClick={() => {
                                      if (portal?.id) {
                                        logPortalActivity(
                                          portal.id,
                                          selectedProjectId,
                                          'link_click',
                                          'viewed_released_version',
                                          {
                                            streamKind: 'two_d_pdf',
                                            releaseId: release.id,
                                            name: release.name,
                                            releaseVersionIndex: release.releaseVersionIndex,
                                            context: 'engineered_drawings_table',
                                          },
                                          activitySource,
                                        );
                                      }
                                      openApprovedPdf(release);
                                    }}
                                  >
                                    <Eye size={14} aria-hidden />
                                    View
                                  </button>
                                  {isDesktopPortal && (
                                    <button
                                      type="button"
                                      className="cps-deliverable-action"
                                      onClick={() => {
                                        setPdfMarkupTourOnOpen(
                                          !hasSeenPortalPdfMarkupTour() && !isStakeholderViewer,
                                        );
                                        openApprovedPdf(release);
                                        if (portal?.id) {
                                          logPortalActivity(
                                            portal.id,
                                            selectedProjectId,
                                            'link_click',
                                            'opened_portal_pdf_markup',
                                            { releaseId: release.id, context: 'engineered_drawings_table' },
                                            activitySource,
                                          );
                                        }
                                      }}
                                    >
                                      Review &amp; mark up
                                      <ChevronRight size={14} aria-hidden />
                                    </button>
                                  )}
                                  {planPackageReleases.length === 1 && primaryPlanRelease && release.id === primaryPlanRelease.id && !isSampleGcPortal && !isStakeholderViewer && (
                                    isDeliverableApproved(effectivePortal?.plansApproval, primaryPlanRelease.id) ? (
                                      <span className="cps-approval-badge">
                                        <CheckCircle size={14} aria-hidden />
                                        {formatApprovalBadge(effectivePortal?.plansApproval)}
                                      </span>
                                    ) : (
                                      <button
                                        type="button"
                                        className="cps-action-btn cps-btn-primary"
                                        onClick={() =>
                                          openDrawingsWalkthrough(
                                            [primaryPlanRelease],
                                            currentPlanBundle?.id,
                                            primaryPlanRelease.releaseVersionIndex,
                                          )
                                        }
                                      >
                                        <FileSignature size={14} />
                                        Approve {drawingApprovalLabel(primaryPlanRelease)}
                                      </button>
                                    )
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                          {planPackageReleases.length > 1 && primaryPlanRelease && !isSampleGcPortal && !isStakeholderViewer && (
                            <tr className="cps-drawings-package-row">
                              <td colSpan={3}>
                                <div className="cps-drawings-package-approval">
                                  {isDeliverableApproved(effectivePortal?.plansApproval, primaryPlanRelease.id) ? (
                                    <span className="cps-approval-badge">
                                      <CheckCircle size={14} aria-hidden />
                                      {formatApprovalBadge(effectivePortal?.plansApproval)}
                                    </span>
                                  ) : (
                                    <>
                                      <p>
                                        Approving{' '}
                                        {formatDrawingList(planPackageReleases.map(drawingApprovalLabel))}
                                      </p>
                                      <button
                                        type="button"
                                        className="cps-action-btn cps-btn-primary"
                                        onClick={() =>
                                          openDrawingsWalkthrough(
                                            planPackageReleases,
                                            currentPlanBundle?.id,
                                            primaryPlanRelease.releaseVersionIndex,
                                          )
                                        }
                                      >
                                        <FileSignature size={14} />
                                        Approve drawings
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                          {engineeringModelPackageReleases.map((release, idx) => (
                            <tr key={release.id}>
                              <td>
                                {idx === 0 && (
                                  <DeliverableLatestVersionCell
                                    releaseVersionIndex={release.releaseVersionIndex}
                                    releasedAt={currentEngineeringModelBundle?.releasedAt ?? release.releasedAt}
                                  />
                                )}
                              </td>
                              <td>
                                <span className="cps-deliverable-name">
                                  <Box size={15} aria-hidden />
                                  {engineeringModelPackageReleases.length === 1
                                    ? '3D Model'
                                    : release.displayLabel || release.name}
                                </span>
                              </td>
                              <td>
                                <div className="cps-deliverable-actions">
                                  <button
                                    type="button"
                                    className="cps-deliverable-action"
                                    onClick={() => {
                                      if (portal?.id) {
                                        logPortalActivity(
                                          portal.id,
                                          selectedProjectId,
                                          'link_click',
                                          'viewed_3d_model',
                                          {
                                            releaseId: release.id,
                                            context: 'engineered_drawings_table',
                                          },
                                          activitySource,
                                        );
                                      }
                                      openReleaseFile(release, release.approval);
                                    }}
                                  >
                                    <ExternalLink size={14} aria-hidden />
                                    View
                                  </button>
                                  {loomUrlForRelease(release) ? (
                                    <a
                                      href={loomUrlForRelease(release)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="cps-deliverable-action"
                                      onClick={() => {
                                        if (!portal?.id) return;
                                        logPortalActivity(
                                          portal.id,
                                          selectedProjectId,
                                          'link_click',
                                          'viewed_3d_loom',
                                          { releaseId: release.id, context: 'engineered_drawings_table' },
                                          activitySource,
                                        );
                                      }}
                                    >
                                      <Video size={14} aria-hidden />
                                      Watch
                                    </a>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Phased projects: phase summary — committed status, future estimates, Review & commit CTA */}
                  {step.id === 'quote-signed' &&
                    (status === 'active' || status === 'completed') &&
                    phasedProposalDoc &&
                    proposalSlugForDisplay && (
                      <PortalPhaseSummaryCard
                        proposal={phasedProposalDoc}
                        onReviewPhase={(anchor) => handleProposalNavigate(proposalSlugForDisplay, anchor)}
                      />
                    )}

                  {/* Project Timeline — shown on quote step when proposal is visible and not yet signed */}
                  {step.id === 'quote' && (status === 'active' || status === 'completed') && proposalStatus && ['sent', 'opened', 'approved'].includes(proposalStatus) && stageOrder.indexOf(ep.currentStage) < stageOrder.indexOf('quote-signed') && (
                    <div>
                      <ProjectTimeline
                        phases={resolvedTimelinePhases}
                        savedTargetDate={savedTargetDate}
                        onTargetDateChange={handleTargetDateChange}
                      />
                    </div>
                  )}

                  {/* Concept Deliverables — 3D model + 2D drawings table */}
                  {step.id === '3d-model' && conceptDeliverablesOpen && (status === 'active' || status === 'completed') && (() => {
                    const loomEmbedSrc = loomEmbedSrcFromPaste(
                      clientFacingModelLoomUrl(primaryConceptModelRelease) || '',
                    );
                    const hasModel =
                      modelPackageReleases.length > 0 ||
                      conceptPackageReleases.length > 0 ||
                      !!ep.modelImageUrl ||
                      (!!ep.modelUrl && releasedModels.length === 0);

                    if (!hasModel) {
                      return (
                        <div style={{ marginTop: 12, padding: '20px 16px', background: '#FAFAF8', border: '1px solid #E8E5E0', borderRadius: 10, textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, borderRadius: '50%', background: '#F0FDF4', marginBottom: 10 }}>
                            <Box size={20} style={{ color: '#2C3E2F' }} />
                          </div>
                          <h4 style={{ margin: '0 0 6px', fontSize: 15, fontWeight: 600, color: '#1a1a1a' }}>We're preparing your concept deliverables</h4>
                          <p style={{ margin: 0, fontSize: 13, color: '#6B7280', lineHeight: 1.5 }}>
                            Our design team is working on your interactive 3D model and concept drawings.
                            We'll notify you as soon as they're ready for review.
                          </p>
                        </div>
                      );
                    }

                    return (
                    <div className="cps-model-section">
                      {loomEmbedSrc && (
                        <div className="cps-loom-embed-wrap" style={{ marginBottom: 16 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                            <Video size={16} style={{ color: '#2C3E2F', flexShrink: 0 }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>Design walkthrough</span>
                          </div>
                          <div
                            style={{
                              position: 'relative',
                              width: '100%',
                              maxWidth: 720,
                              margin: '0 auto',
                              borderRadius: 10,
                              overflow: 'hidden',
                              border: '1px solid #E8E5E0',
                              background: '#0f0f0f',
                              aspectRatio: '16 / 9',
                            }}
                          >
                            <iframe
                              title="Loom walkthrough"
                              src={loomEmbedSrc}
                              allowFullScreen
                              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                              onLoad={() => {
                                if (portal?.id) {
                                  logPortalActivity(portal.id, selectedProjectId, 'link_click', 'viewed_3d_loom', undefined, activitySource);
                                }
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {(modelPackageReleases.length > 0 || conceptPackageReleases.length > 0) && (
                        <div className="cps-deliverables-table-wrap">
                          <table
                            className={`cps-deliverables-table${
                              showDeliverableLatestColumn ? ' cps-deliverables-table--with-latest' : ''
                            }`}
                          >
                            <thead>
                              <tr>
                                {showDeliverableLatestColumn && <th scope="col">Latest</th>}
                                <th scope="col">Deliverable</th>
                                <th scope="col">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {modelPackageReleases.map((release, idx) => (
                                <tr key={release.id}>
                                  {showDeliverableLatestColumn && (
                                    <td>
                                      {idx === 0 && (
                                        <DeliverableLatestVersionCell
                                          releaseVersionIndex={release.releaseVersionIndex}
                                          releasedAt={
                                            currentConceptModelBundle?.releasedAt ?? release.releasedAt
                                          }
                                        />
                                      )}
                                    </td>
                                  )}
                                  <td>
                                    <span className="cps-deliverable-name">
                                      <Box size={15} aria-hidden />
                                      {modelPackageReleases.length === 1
                                        ? '3D Model'
                                        : release.displayLabel || release.name}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="cps-deliverable-actions">
                                      <button
                                        type="button"
                                        className="cps-deliverable-action"
                                        onClick={() => {
                                          if (portal?.id) {
                                            logPortalActivity(
                                              portal.id,
                                              selectedProjectId,
                                              'link_click',
                                              'viewed_3d_model',
                                              {
                                                releaseId: release.id,
                                                context: 'deliverables_table',
                                              },
                                              activitySource,
                                            );
                                          }
                                          openReleaseFile(release, approvalForOpenedRelease(release));
                                        }}
                                      >
                                        <ExternalLink size={14} aria-hidden />
                                        View
                                      </button>
                                      {loomUrlForRelease(release) ? (
                                        <a
                                          href={loomUrlForRelease(release)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="cps-deliverable-action"
                                          onClick={() => {
                                            if (!portal?.id) return;
                                            logPortalActivity(
                                              portal.id,
                                              selectedProjectId,
                                              'link_click',
                                              'viewed_3d_loom',
                                              {
                                                releaseId: release.id,
                                                context: 'deliverables_table',
                                              },
                                              activitySource,
                                            );
                                          }}
                                        >
                                          <Video size={14} aria-hidden />
                                          Watch
                                        </a>
                                      ) : null}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                              {conceptPackageReleases.map((release, idx) => (
                                <tr key={release.id}>
                                  {showDeliverableLatestColumn && (
                                    <td>
                                      {idx === 0 && (
                                        <DeliverableLatestVersionCell
                                          releaseVersionIndex={release.releaseVersionIndex}
                                          releasedAt={
                                            currentConceptBundle?.releasedAt ?? release.releasedAt
                                          }
                                        />
                                      )}
                                    </td>
                                  )}
                                  <td>
                                    <span className="cps-deliverable-name">
                                      <Layers2 size={15} aria-hidden />
                                      {conceptPackageReleases.length === 1
                                        ? '2D Drawings'
                                        : release.displayLabel || release.name}
                                    </span>
                                  </td>
                                  <td>
                                    <div className="cps-deliverable-actions">
                                      <a
                                        href={portalHostedAssetHref(release.releasedUrl, {
                                          downloadName: release.name || 'concept-elevations.pdf',
                                        })}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="cps-deliverable-action"
                                        download
                                        onClick={() => {
                                          if (portal?.id) {
                                            logPortalActivity(
                                              portal.id,
                                              selectedProjectId,
                                              'link_click',
                                              'downloaded_concept_elevations',
                                              { releaseId: release.id },
                                              activitySource,
                                            );
                                          }
                                        }}
                                      >
                                        <Download size={14} aria-hidden />
                                        Download
                                      </a>
                                      {isDesktopPortal && (
                                        <button
                                          type="button"
                                          className="cps-deliverable-action"
                                          onClick={() => {
                                            if (!portal?.id) return;
                                            logPortalActivity(
                                              portal.id,
                                              selectedProjectId,
                                              'link_click',
                                              'opened_portal_pdf_markup',
                                              {
                                                releaseId: release.id,
                                                context: 'deliverables_table',
                                              },
                                              activitySource,
                                            );
                                            openApprovedPdf(release);
                                          }}
                                          aria-label={`Comment and draw on ${release.displayLabel || release.name}`}
                                        >
                                          <MessageSquare size={14} aria-hidden />
                                          Comment &amp; draw
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {ep.modelUrl && releasedModels.length === 0 && (
                        <div className="cps-step-actions" style={{ marginTop: 12 }}>
                          <a
                            href={portalHostedAssetHref(ep.modelUrl, {
                              downloadName:
                                portal?.slug === SAMPLE_GC_PORTAL_SLUG
                                  ? `${SAMPLE_GC_MODEL_PAGE_TITLE}.html`
                                  : '3D Model.html',
                            })}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="cps-action-btn cps-btn-secondary"
                            onClick={() => {
                              if (portal?.id) {
                                logPortalActivity(
                                  portal.id,
                                  selectedProjectId,
                                  'link_click',
                                  'viewed_3d_model',
                                  { context: 'model_url_external' },
                                  activitySource,
                                );
                                beginPortal3dModelExternalTabSession(
                                  portal.id,
                                  selectedProjectId,
                                  activitySource,
                                );
                              }
                            }}
                          >
                            <ExternalLink size={14} aria-hidden />
                            View 3D model
                          </a>
                        </div>
                      )}

                      {ep.modelUrl &&
                        modelWalkthroughEnabled &&
                        isDesktopPortal &&
                        !isStakeholderViewer && (
                        <div style={{ marginTop: 12, marginBottom: 4 }}>
                          <button
                            type="button"
                            className="cps-action-btn cps-btn-secondary"
                            onClick={() => {
                              setShowWalkthroughRecorder(true);
                              if (portal?.id) {
                                logPortalActivity(
                                  portal.id,
                                  selectedProjectId,
                                  'action',
                                  'started_model_walkthrough',
                                  { releaseId: primaryConceptModelRelease?.id },
                                  activitySource,
                                );
                              }
                            }}
                          >
                            <Video size={14} aria-hidden />
                            Record walkthrough
                            <span className="pmwr-beta" style={{ marginLeft: 6 }}>Beta</span>
                          </button>
                          <p style={{ margin: '8px 0 0', fontSize: 12, color: '#6b7280' }}>
                            Record a short video while you explore your 3D model (up to 5 minutes).
                          </p>
                        </div>
                      )}

                      {!isSampleGcPortal &&
                        !isStakeholderViewer &&
                        !proposalIsRom &&
                        !isConceptReviewRetired({
                          proposalStatus,
                          currentStage: ep.currentStage,
                        }) &&
                        isConceptApprovalFlowEnabled({
                          flowDisabled: ep.conceptApprovalFlowDisabled,
                          proposalStatus,
                        }) &&
                        (ep.modelUrl ||
                          ep.modelImageUrl ||
                          ep.conceptElevationUrl ||
                          primaryConceptRelease) && (
                        <div className="cps-model-approval-row">
                          {(() => {
                            const needsInteractive = needsInteractiveModelDeliverable({
                              modelUrl: ep.modelUrl,
                              modelStepSkipped: ep.modelStepSkipped,
                            });
                            const needsImage = needsPreviewImageDeliverable({
                              modelUrl: ep.modelUrl,
                              modelImageUrl: ep.modelImageUrl,
                              modelStepSkipped: ep.modelStepSkipped,
                            });
                            const needsConceptPdf = needsConceptPdfDeliverable({
                              conceptElevationUrl: ep.conceptElevationUrl,
                              conceptReleaseId: primaryConceptRelease?.id,
                            });
                            const interactiveOk =
                              !needsInteractive ||
                              isInteractiveModelApproved(
                                effectivePortal?.modelApproval,
                                primaryModelRelease?.id,
                              );
                            const imageOk =
                              !needsImage ||
                              isPreviewImageApproved(
                                effectivePortal?.modelApproval,
                                ep.modelImageUrl,
                              );
                            const conceptOk =
                              !needsConceptPdf ||
                              isDeliverableApproved(
                                effectivePortal?.conceptApproval,
                                primaryConceptRelease?.id,
                              );
                            const allApproved = interactiveOk && imageOk && conceptOk;
                            const needsApproval = !allApproved;

                            if (allApproved) {
                              return (
                                <div className="cps-approval-status-group">
                                  {needsInteractive && effectivePortal?.modelApproval && (
                                    <span className="cps-approval-badge">
                                      <CheckCircle size={14} aria-hidden />
                                      3D {formatApprovalBadge(effectivePortal.modelApproval)}
                                    </span>
                                  )}
                                  {needsImage && effectivePortal?.modelApproval && (
                                    <span className="cps-approval-badge">
                                      <CheckCircle size={14} aria-hidden />
                                      Preview {formatApprovalBadge(effectivePortal.modelApproval)}
                                    </span>
                                  )}
                                  {needsConceptPdf && effectivePortal?.conceptApproval && (
                                    <span className="cps-approval-badge">
                                      <CheckCircle size={14} aria-hidden />
                                      2D {formatApprovalBadge(effectivePortal.conceptApproval)}
                                    </span>
                                  )}
                                </div>
                              );
                            }

                            if (needsApproval) {
                              return (
                                <>
                                  {(needsInteractive || needsImage) && (
                                    <p className="cps-model-approval-note">
                                      {CONCEPT_MODEL_APPROVAL_DISCLAIMER}
                                    </p>
                                  )}
                                  <button
                                    type="button"
                                    className="cps-action-btn cps-btn-primary"
                                    onClick={() => {
                                      if (portal?.id) {
                                        logPortalActivity(
                                          portal.id,
                                          selectedProjectId,
                                          'action',
                                          'opened_design_approval_walkthrough',
                                          undefined,
                                          activitySource,
                                        );
                                      }
                                      setDesignApprovalWalkthroughOpen(true);
                                    }}
                                  >
                                    <FileSignature size={14} />
                                    Review &amp; Approve
                                    <ChevronRight size={16} />
                                  </button>
                                </>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}

                      {!ep.modelUrl && ep.modelImageUrl && (
                        <div className="cps-model-image-preview">
                          <img src={portalHostedAssetHref(ep.modelImageUrl)} alt="Design preview" />
                        </div>
                      )}

                      {ep.modelNotes && (
                        <div className="cps-model-notes-employee">
                          <Briefcase size={14} />
                          <p>{ep.modelNotes}</p>
                        </div>
                      )}
                    </div>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
        )}
        
        {/* Supplementary deliverable tracks (only when releases exist) */}
        {supplementarySections.map(section => {
          const heading = section.stream.portalDisplayName || section.stream.title;
          const currentBundle = section.bundles[0];
          const packageReleases = currentPackageReleases(currentBundle, section.releases);
          const primaryRelease = packageReleases[0];
          if (!primaryRelease) return null;
          return (
            <section key={section.streamId} className="cps-supplementary-section">
              <h3 className="cps-supplementary-heading">{heading}</h3>
              <p className="cps-supplementary-intro">Additional project deliverables released for your review.</p>
              {packageReleases.length === 1 ? (
                <div className="cps-step-actions">
                  <a
                    href={portalHostedAssetHref(primaryRelease.releasedUrl, { downloadName: primaryRelease.name })}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cps-action-btn cps-btn-secondary"
                    onClick={() => {
                      if (portal?.id) {
                        logPortalActivity(
                          portal.id,
                          selectedProjectId,
                          'link_click',
                          'viewed_supplementary_release',
                          { streamId: section.streamId, releaseId: primaryRelease.id },
                          activitySource,
                        );
                      }
                    }}
                  >
                    View {heading}
                    <ExternalLink size={14} />
                  </a>
                  <PortalReleasedVersionsPopover
                    bundles={section.bundles}
                    releases={section.releases}
                    heading={heading}
                    onOpenRelease={r => {
                      if (portal?.id) {
                        logPortalActivity(
                          portal.id,
                          selectedProjectId,
                          'link_click',
                          'viewed_supplementary_release',
                          { streamId: section.streamId, releaseId: r.id },
                          activitySource,
                        );
                      }
                    }}
                  />
                </div>
              ) : (
                <ul className="cps-supplementary-file-list">
                  {packageReleases.map(release => (
                    <li key={release.id}>
                      <a
                        href={portalHostedAssetHref(release.releasedUrl, { downloadName: release.name })}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cps-action-btn cps-btn-secondary"
                        onClick={() => {
                          if (portal?.id) {
                            logPortalActivity(
                              portal.id,
                              selectedProjectId,
                              'link_click',
                              'viewed_supplementary_release',
                              { streamId: section.streamId, releaseId: release.id },
                              activitySource,
                            );
                          }
                        }}
                      >
                        {release.displayLabel || release.name}
                        <ExternalLink size={14} />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
              {packageReleases.length > 1 && (
                <PortalReleasedVersionsPopover
                  bundles={section.bundles}
                  releases={section.releases}
                  heading={heading}
                  onOpenRelease={r => {
                    if (portal?.id) {
                      logPortalActivity(
                        portal.id,
                        selectedProjectId,
                        'link_click',
                        'viewed_supplementary_release',
                        { streamId: section.streamId, releaseId: r.id },
                        activitySource,
                      );
                    }
                  }}
                />
              )}
            </section>
          );
        })}

        {/* Surety bond status */}
        {selectedProjectId && portalBond && (
          <section className={`cps-co-section cps-bond-section is-${portalBond.copy.tone}`} aria-label="Surety bond">
            <h3 className="cps-co-heading">
              <ShieldCheck size={18} /> Surety Bond
            </h3>
            <div className="cps-bond-body">
              <div className="cps-bond-main">
                <span className={`cps-bond-pill cps-bond-pill--lg is-${portalBond.copy.tone}`}>
                  <ShieldCheck size={13} aria-hidden /> {portalBond.copy.pill}
                </span>
                <strong className="cps-bond-title">{portalBond.copy.title}</strong>
                <p className="cps-bond-detail">{portalBond.copy.detail}</p>
              </div>
              <dl className="cps-bond-facts">
                <div>
                  <dt>Bond type</dt>
                  <dd>{portalBond.bond.bondType === 'bid' ? 'Bid bond' : 'Performance & Payment'}</dd>
                </div>
                <div>
                  <dt>{portalBond.bond.bondType === 'bid' ? 'Bond amount' : 'Amount of each bond'}</dt>
                  <dd>{formatPortalBondAmount(portalBond.bond.bondAmount)}</dd>
                </div>
                {portalBond.bond.suretyName && (
                  <div>
                    <dt>Surety</dt>
                    <dd>{portalBond.bond.suretyName}</dd>
                  </div>
                )}
                {portalBond.bond.bondNumber && (
                  <div>
                    <dt>Bond number</dt>
                    <dd>{portalBond.bond.bondNumber}</dd>
                  </div>
                )}
                <div>
                  <dt>{portalBond.bond.status === 'issued' && portalBond.bond.issuedAt ? 'Issued' : 'Requested'}</dt>
                  <dd>
                    {new Date(
                      `${portalBond.bond.status === 'issued' && portalBond.bond.issuedAt ? portalBond.bond.issuedAt : portalBond.bond.letterDate}T12:00:00`,
                    ).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </dd>
                </div>
              </dl>
            </div>
          </section>
        )}

        {/* Change Orders */}
        {selectedProjectId && visibleChangeOrders.length > 0 && (
          <section className="cps-co-section">
            <h3 className="cps-co-heading">
              <FileSignature size={18} /> Change Orders
            </h3>
            {allVisibleChangeOrdersApproved && signedProposalBase != null && currentContractValue != null ? (
              <div className="cps-co-contract-summary">
                <span className="cps-co-contract-stat">
                  <span className="cps-co-contract-label">Original contract</span>
                  <span className="cps-co-contract-value">
                    ${Math.round(signedProposalBase).toLocaleString('en-US')}
                  </span>
                </span>
                <span className="cps-co-contract-arrow" aria-hidden>→</span>
                <span className="cps-co-contract-stat">
                  <span className="cps-co-contract-label">Current contract</span>
                  <span className="cps-co-contract-value">
                    ${Math.round(currentContractValue).toLocaleString('en-US')}
                  </span>
                </span>
              </div>
            ) : (
              <p className="cps-co-intro">
                Updates to your project scope and contract. Review the details and sign to approve.
              </p>
            )}
            <div className="cps-co-list">
              {visibleChangeOrders.map((co) => {
                const approved = co.status === 'approved';
                const isInternal = isInternallyApprovedChangeOrder(co);
                const isCredit = co.deltaAmount < 0;
                const absLabel = `$${Math.abs(Math.round(co.deltaAmount)).toLocaleString('en-US')}`;
                // Accounting convention: credits shown in parentheses, additions with a leading +.
                const deltaLabel = isCredit ? `(${absLabel})` : `+${absLabel}`;
                return (
                  <div
                    key={co.id}
                    className={`cps-co-card ${approved ? 'approved' : 'pending'}${isInternal ? ' cps-co-card--internal' : ''}`}
                  >
                    <div className="cps-co-card-main">
                      <div className="cps-co-card-top">
                        <span className="cps-co-ref">{getChangeOrderReference(co)}</span>
                        <span className={`cps-co-status ${approved ? 'approved' : 'pending'}`}>
                          {approved ? (
                            <><CheckCircle size={13} /> Approved</>
                          ) : (
                            <><FileSignature size={13} /> Awaiting your signature</>
                          )}
                        </span>
                        {isInternal && (
                          <ChangeOrderInternalBadge recordedBy={co.internalApprovedBy} className="cps-co-internal-badge" />
                        )}
                      </div>
                      <div className="cps-co-title">{co.title}</div>
                      {co.scopeDescription && (
                        <div className="cps-co-scope">{co.scopeDescription}</div>
                      )}
                    </div>
                    <div className="cps-co-card-side">
                      <span className={`cps-co-amount ${co.deltaAmount < 0 ? 'neg' : 'pos'}`}>{deltaLabel}</span>
                      {approved ? (
                        co.signedPdfUrl ? (
                          <a
                            className="cps-co-btn secondary"
                            href={co.signedPdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <FileText size={14} /> View{isInternal ? '' : ' signed'}
                          </a>
                        ) : (
                          <button className="cps-co-btn secondary" onClick={() => handleChangeOrderNavigate(co)}>
                            <FileText size={14} /> View
                          </button>
                        )
                      ) : isStakeholderViewer ? (
                        <span className="cps-co-viewonly">View only</span>
                      ) : (
                        <button className="cps-co-btn" onClick={() => handleChangeOrderNavigate(co)}>
                          <FileSignature size={14} /> Review &amp; sign
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Contract Payment Schedule */}
        {selectedProjectId && signedProposalBase != null && (
          <section className="cps-co-section">
            <h3 className="cps-co-heading">
              <FileText size={18} /> Payment Schedule
              <PortalStatementButton
                projectName={portalStatementProject.descriptor || portal?.clientName || 'Project'}
                clientCompany={portalStatementProject.clientCompany}
                clientName={portal?.clientName}
                projectNumber={portalStatementProject.projectNumber}
                originalContract={signedProposalBase}
                changeOrderTotal={(currentContractValue ?? signedProposalBase) - signedProposalBase}
                currentContractValue={currentContractValue ?? signedProposalBase}
                changeOrders={visibleChangeOrders}
                schedule={portalScheduleBreakdown.rows.map((row) => ({
                  id: row.id,
                  label: row.label,
                  detail: row.detail,
                  amount: row.amount,
                  kind: row.kind,
                  baseAmount: row.baseAmount,
                  folded: row.coAdjustments.map((adj) => ({
                    reference: adj.reference,
                    title: adj.title,
                    amount: adj.amount,
                  })),
                }))}
                statuses={portalInstallmentStatuses}
                activity={effectivePortal?.statement || []}
              />
            </h3>
            <p className="cps-co-intro">
              Your contract payment installments{portalScheduleBreakdown.hasChangeOrders
                ? ', including approved change orders'
                : ''}.
            </p>
            <PaymentScheduleBreakdown
              breakdown={portalScheduleBreakdown}
              variant="light"
              compact
              statuses={portalInstallmentStatuses}
            />
          </section>
        )}

        {/* Resources & Documents */}
        {engagementDocuments.length > 0 && (
          <section className="cps-documents-section">
            <div className="cps-documents-heading-row">
              <h3 className="cps-documents-heading">Resources & Documents</h3>
              <span className="cps-documents-additional">
                Additional
                <button
                  type="button"
                  className="cps-documents-help"
                  aria-label="Specialty insurance and bonding"
                >
                  <HelpCircle size={15} />
                  <span className="cps-documents-help-tip" role="tooltip">
                    Leiper's Creek Timber Frames can also provide specialty insurance or bonding, if it is necessary for the project.
                  </span>
                </button>
              </span>
            </div>
            <p className="cps-documents-intro">
              Access helpful information about our services:
            </p>
            <div className="cps-document-links">
              {engagementDocuments.map((d) => (
                <a
                  key={d.id}
                  href={portalHostedAssetHref(d.url, { downloadName: d.name })}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cps-document-link"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                  <span>{d.name}</span>
                </a>
              ))}
            </div>
          </section>
        )}

        {/* Footer */}
        <footer className="cps-footer">
          <p>
            Questions about your project?{' '}
            <a href="mailto:info@lctimberframes.com">Contact us</a>
          </p>
          <p className="cps-copyright">
            © {new Date().getFullYear()} Leiper's Creek Timber Frames
          </p>
        </footer>
      </div>

      {/* Lightbox */}
      {lightboxOpen && lightboxImages.length > 0 && (
        <div className="cps-lightbox-overlay" onClick={closeLightbox}>
          <div className="cps-lightbox" onClick={(e) => e.stopPropagation()}>
            <button className="cps-lightbox-close" onClick={closeLightbox}>
              <X size={24} />
            </button>
            <img
              src={lightboxImages[lightboxIndex].url}
              alt={lightboxImages[lightboxIndex].name}
              className="cps-lightbox-img"
            />
            <p className="cps-lightbox-name">{lightboxImages[lightboxIndex].name}</p>
            {lightboxImages.length > 1 && (
              <div className="cps-lightbox-nav">
                <button
                  onClick={() => setLightboxIndex((lightboxIndex - 1 + lightboxImages.length) % lightboxImages.length)}
                  className="cps-lightbox-arrow"
                >
                  <ChevronRight size={24} style={{ transform: 'rotate(180deg)' }} />
                </button>
                <span className="cps-lightbox-counter">{lightboxIndex + 1} / {lightboxImages.length}</span>
                <button
                  onClick={() => setLightboxIndex((lightboxIndex + 1) % lightboxImages.length)}
                  className="cps-lightbox-arrow"
                >
                  <ChevronRight size={24} />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Portal PDF viewer — View and the drawings approval walkthrough open here. */}
      {htmlViewer && (
        <PortalHtmlViewerModal
          url={htmlViewer.url}
          title={htmlViewer.title}
          approval={htmlViewer.approval}
          sharedModelNote={htmlViewer.sharedModelNote}
          onAdvanceToNextFile={canAdvanceReview ? advanceReviewFile : undefined}
          onApprove={canApproveOpenFile ? approveFromOpenFile : undefined}
          onClose={closeFileViewers}
        />
      )}

      {pdfViewerRelease && portal?.id && (
        <PortalPdfViewerModal
          url={pdfViewerRelease.releasedUrl}
          title={pdfViewerRelease.displayLabel || pdfViewerRelease.name}
          approval={pdfViewerApproval}
          portalId={portal.id}
          dealId={selectedProjectId || pdfViewerRelease.dealId}
          releaseId={pdfViewerRelease.id || ''}
          bundleId={pdfViewerBundleId}
          projectName={portal.clientName}
          isStaffPortalUser={isStaffPortalUser}
          clientDisplayName={portal.clientName}
          startTourOnOpen={pdfMarkupTourOnOpen}
          activitySource={activitySource}
          onAdvanceToNextFile={canAdvanceReview ? advanceReviewFile : undefined}
          onApprove={canApproveOpenFile ? approveFromOpenFile : undefined}
          onClose={closeFileViewers}
        />
      )}

      {approvalModal && portal?.id && selectedProjectId && !isSampleGcPortal && (
        <PortalDeliverableApprovalModal
          open
          onClose={() => setApprovalModal(null)}
          portalId={portal.id}
          dealId={selectedProjectId}
          deliverableType={approvalModal.type}
          deliverableLabel={
            approvalModal.label ||
            (approvalModal.type === 'plans'
              ? 'Engineered Drawings'
              : approvalModal.type === 'concept'
                ? '2D Concept Elevations'
                : '3D Model')
          }
          releaseId={approvalModal.releaseId}
          bundleId={approvalModal.bundleId}
          releaseVersionIndex={approvalModal.releaseVersionIndex}
          releaseIds={approvalModal.releaseIds}
          sharedModelNote={approvalModal.sharedModelNote}
          onApproved={() => rememberDeliverableApproval(approvalModal)}
        />
      )}

      {drawingsWalkthrough && portal?.id && selectedProjectId && !isSampleGcPortal && (
        <PortalDrawingsApprovalWalkthrough
          open
          onClose={() => {
            setInitialsApprovalOpen(false);
            setDrawingsWalkthrough(null);
          }}
          portalId={portal.id}
          dealId={selectedProjectId}
          releases={drawingsWalkthrough.releases}
          bundleId={drawingsWalkthrough.bundleId}
          releaseVersionIndex={drawingsWalkthrough.releaseVersionIndex}
          activitySource={activitySource}
          onOpenDrawing={release => {
            setPdfMarkupTourOnOpen(!hasSeenPortalPdfMarkupTour() && !isStakeholderViewer && isDesktopPortal);
            openReleaseFile(release, null, drawingsWalkthrough.releases);
          }}
          viewingReleaseId={reviewQueue?.[reviewIndex]?.id}
          initialsModalOpen={initialsApprovalOpen}
          onInitialsModalClose={() => {
            setInitialsApprovalOpen(false);
            if (initialsFromViewerRef.current) {
              initialsFromViewerRef.current = false;
              setDrawingsWalkthrough(null);
            }
          }}
          onApprovalFinished={() => {
            setInitialsApprovalOpen(false);
            closeFileViewers();
            setDrawingsWalkthrough(null);
          }}
          onApproved={() =>
            rememberDeliverableApproval({
              type: 'plans',
              releaseId: drawingsWalkthrough.releases[0]?.id,
              bundleId: drawingsWalkthrough.bundleId,
              releaseVersionIndex:
                drawingsWalkthrough.releaseVersionIndex ??
                drawingsWalkthrough.releases[0]?.releaseVersionIndex,
            })
          }
        />
      )}

      {portal?.id && selectedProjectId && timelinePortal && !isSampleGcPortal && (
        <PortalDesignApprovalWalkthrough
          open={designApprovalWalkthroughOpen}
          onClose={() => setDesignApprovalWalkthroughOpen(false)}
          portalId={portal.id}
          dealId={selectedProjectId}
          modelUrl={timelinePortal.modelUrl}
          modelImageUrl={effectivePortal?.modelImageUrl}
          modelRelease={primaryModelRelease}
          modelBundleId={currentModelBundle?.id}
          modelApproval={effectivePortal?.modelApproval}
          conceptUrl={timelinePortal.conceptElevationUrl || primaryConceptRelease?.releasedUrl}
          conceptRelease={primaryConceptRelease}
          conceptBundleId={currentConceptBundle?.id}
          conceptApproval={effectivePortal?.conceptApproval}
          isDesktopPortal={isDesktopPortal}
          activitySource={activitySource}
          onOpenConceptFeedback={() => openConceptPdfMarkup('design_approval_walkthrough')}
          canSignProposal={canOfferSignProposal}
          onSignProposal={
            proposalSlugForDisplay
              ? () => {
                  setDesignApprovalWalkthroughOpen(false);
                  handleProposalNavigate(proposalSlugForDisplay, 'proposal-signature');
                }
              : undefined
          }
          onModelApproved={() => {
            setPortal(prev => {
              if (!prev || !selectedProjectId) return prev;
              const nextApproval = {
                status: 'approved' as const,
                approvedAt: new Date(),
                approvedByName: user?.name || user?.email || 'Client',
                signature: '',
                releaseId: primaryModelRelease?.id,
                bundleId: currentModelBundle?.id,
                releaseVersionIndex: primaryModelRelease?.releaseVersionIndex,
                assetUrl: effectivePortal?.modelImageUrl?.trim() || undefined,
              };
              const project = prev.projects?.[selectedProjectId];
              const archived = nextApprovalWithHistory(
                project?.modelApproval ?? prev.modelApproval,
                project?.modelApprovalHistory ?? prev.modelApprovalHistory,
                nextApproval,
              );
              const updated = { ...prev };
              if (updated.projects?.[selectedProjectId]) {
                updated.projects = {
                  ...updated.projects,
                  [selectedProjectId]: {
                    ...updated.projects[selectedProjectId],
                    modelApproval: archived.current,
                    modelApprovalHistory: archived.history,
                  },
                };
              }
              if (prev.dealId === selectedProjectId || !isMultiProjectPortal(prev)) {
                updated.modelApproval = archived.current;
                updated.modelApprovalHistory = archived.history;
              }
              return updated;
            });
          }}
          onConceptApproved={() => {
            setPortal(prev => {
              if (!prev || !selectedProjectId) return prev;
              const nextApproval = {
                status: 'approved' as const,
                approvedAt: new Date(),
                approvedByName: user?.name || user?.email || 'Client',
                signature: '',
                releaseId: primaryConceptRelease?.id,
                bundleId: currentConceptBundle?.id,
                releaseVersionIndex: primaryConceptRelease?.releaseVersionIndex,
              };
              const project = prev.projects?.[selectedProjectId];
              const archived = nextApprovalWithHistory(
                project?.conceptApproval ?? prev.conceptApproval,
                project?.conceptApprovalHistory ?? prev.conceptApprovalHistory,
                nextApproval,
              );
              const updated = { ...prev };
              if (updated.projects?.[selectedProjectId]) {
                updated.projects = {
                  ...updated.projects,
                  [selectedProjectId]: {
                    ...updated.projects[selectedProjectId],
                    conceptApproval: archived.current,
                    conceptApprovalHistory: archived.history,
                  },
                };
              }
              if (prev.dealId === selectedProjectId || !isMultiProjectPortal(prev)) {
                updated.conceptApproval = archived.current;
                updated.conceptApprovalHistory = archived.history;
              }
              return updated;
            });
          }}
        />
      )}

      {showWalkthroughRecorder && portal?.id && selectedProjectId && timelinePortal?.modelUrl && (
        <PortalModelWalkthroughRecorder
          open={showWalkthroughRecorder}
          onClose={() => setShowWalkthroughRecorder(false)}
          portalId={portal.id}
          dealId={selectedProjectId}
          modelUrl={timelinePortal.modelUrl}
          releaseId={primaryModelRelease?.id}
          bundleId={currentModelBundle?.id}
          projectName={multiProjectContext?.parsed.descriptor || portal.clientName}
          clientDisplayName={portal.clientCompany || portal.clientName}
          isStaffPortalUser={isStaffPortalUser}
          onSubmitted={() => {
            if (portal?.id) {
              logPortalActivity(
                portal.id,
                selectedProjectId,
                'action',
                'submitted_model_walkthrough',
                { releaseId: primaryModelRelease?.id },
                activitySource,
              );
            }
            setShowWalkthroughRecorder(false);
          }}
        />
      )}

      {/* Design Fee Sign & Pay Modal */}
      {portal && (
        <DesignFeeSignModal
          open={showSignModal && !isStakeholderViewer}
          onClose={() => {
            setShowSignModal(false);
            if (portal?.id) logPortalActivity(portal.id, selectedProjectId, 'action', 'closed_sign_modal', undefined, activitySource);
          }}
          portalId={portal.id || ''}
          portalSlug={portal.slug || slug || ''}
          engagementLetterId={effectivePortal?.engagementLetterId}
          selectedServices={designFeeServices.filter(s => selectedServices.includes(s.id))}
          totalAmount={designFeeServices.filter(s => selectedServices.includes(s.id)).reduce((sum, s) => sum + s.price, 0)}
          clientName={portal.clientName || ''}
          projectId={selectedProjectId || undefined}
          onAgreementSigned={() => {
            setEngagementSigned(true);
            if (portal?.id) logPortalActivity(portal.id, selectedProjectId, 'action', 'agreement_signed', undefined, activitySource);
          }}
          onPaymentComplete={(amountPaid) => {
            if (portal?.id) logPortalActivity(portal.id, selectedProjectId, 'action', 'payment_completed', { amountPaid }, activitySource);
            const paidAt = new Date();
            setPortal(prev => {
              if (!prev) return prev;
              const projectKeys = prev.projects ? Object.keys(prev.projects) : [];
              const mirrorRoot =
                !selectedProjectId ||
                projectKeys.length <= 1 ||
                (!!prev.dealId && selectedProjectId === prev.dealId);
              let next: ClientPortal = { ...prev };
              if (selectedProjectId && next.projects?.[selectedProjectId]) {
                next = {
                  ...next,
                  projects: {
                    ...next.projects,
                    [selectedProjectId]: {
                      ...next.projects[selectedProjectId],
                      designFeePaid: true,
                      designFeePaidAt: paidAt,
                      amountPaid,
                      currentStage: '3d-model',
                    },
                  },
                };
              }
              if (mirrorRoot) {
                next = {
                  ...next,
                  designFeePaid: true,
                  designFeePaidAt: paidAt,
                  amountPaid,
                  currentStage: '3d-model',
                };
              }
              return next;
            });
          }}
        />
      )}

      {/* Sample 3D Model Viewer */}
      {showSample3D && sample3DModelUrl && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
          onClick={() => {
            setShowSample3D(false);
            if (portal?.id) logPortalActivity(portal.id, selectedProjectId, 'action', 'closed_sample_3d_model', undefined, activitySource);
          }}
        >
          <div
            style={{
              background: '#fff', borderRadius: 12, width: '100%', maxWidth: 900,
              height: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Box size={16} style={{ color: '#2C3E2F' }} />
                <span style={{ fontSize: 15, fontWeight: 600, color: '#1a1a1a' }}>Sample 3D Model</span>
              </div>
              <button
                onClick={() => {
                  setShowSample3D(false);
                  if (portal?.id) logPortalActivity(portal.id, selectedProjectId, 'action', 'closed_sample_3d_model', undefined, activitySource);
                }}
                style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer', color: '#6B7280', borderRadius: 6 }}
              >
                <X size={18} />
              </button>
            </div>
            <iframe
              src={sample3DModelUrl}
              title="Sample 3D Model"
              style={{ flex: 1, border: 'none', width: '100%' }}
              allow="fullscreen"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPortalSplash;
